/**
 * ResourcesSingle.tsx
 *
 * Main container for displaying and editing a single resource record in the application.
 * Handles dynamic layouts, form state, resource-specific actions, and integrates with Redux and GraphQL.
 
 */
import { Input, Modal } from "antd";
import restClient from "api/restClient";
import { Accordion } from "components/Accordion/Accordion";
import { Button } from "components/Button/Button";
import { Callout } from "components/Form/components/Callout/Callout";
import { getInitialQueryWithTableSettings, getTableSettings } from "components/Table/tableUtils";
import { Form } from "components/Form/components/Form/Form";
import { FormComponentPropLanguages } from "components/Form/components/FormComponentProps";
import { resolveFileDisplay } from "config/resources/fileDisplay";
import {
    LazyResizeableCodeEditor,
    LazySchemaFormFormComponent,
} from "components/Form/lazyFormComponents";
import type { SchematizedValues } from "components/Form/components/SchemaForm/SchemaForm.types";
import { IconCollection } from "components/Icon/IconCollection";
import { getIconSelector } from "components/ImageIcon/ImageIcon";
import PageContent from "components/Layout/PageContent";
import Placeholder from "components/Layout/Placeholder";
import NotificationService from "components/Notification/Notification.service";
import RestErrorNotification from "components/Notification/RestErrorNotification";
import PageLoader from "components/PageLoader";
import { BlockingNavigationModal } from "components/Prompt/BlockingNavigationModal";
import { NAVIGATION_PRIORITIES } from "components/Prompt/NavigationPromptContext";
import { RecordPrompt } from "components/Prompt/RecordPrompt";
import StateTabs from "components/StateTabs";
import { TimeAgoWithDateTimeFormatter } from "components/TimeAgo/TimeAgoWithDateTimeFormatter";
import type {
    DashboardItemConfig,
    DashboardSectionConfig,
    DashboardWidgetConfig,
} from "config/config";
import { getWriteOnlyFieldsWithData, isSubrecordOrRelationshipType } from "config/resourceUtils";
import bundleConfig from "config/resources/bundle";
import connectorConfig from "config/resources/connector";
import customObjectConfig from "config/resources/custom_object";
import executionConfig, { outputTabKey, scriptTabKey } from "config/resources/execution";
import fileConfig from "config/resources/file";
import flowConfig from "config/resources/flow";
import objectTemplateConfig from "config/resources/object_template";
import projectConfig from "config/resources/project";
import roleConfig from "config/resources/role";
import scheduleConfig from "config/resources/schedule";
import schedulerConfig from "config/resources/scheduler";
import settingConfig from "config/resources/setting";
import userConfig from "config/resources/user";
import webhookConfig from "config/resources/webhook";
import wrapperConfig from "config/resources/wrapper";
import { actions as authActions } from "containers/Auth/Auth.reducer";
import { fetchSingleBundleRepository } from "containers/BundleManager/utils";
import { ExecutionSingleStatus } from "containers/Executions/components/ExecutionsSingleStatus/ExecutionSingleStatus";
import ExecutionsListing from "containers/Executions/screens/ExecutionsListing";
import SavepointsListing from "containers/Executions/screens/SavepointsListing";
import { useMatchingFields } from "containers/Filter/FilterBuilder/useMatchingFields";
import { MessageEditFields } from "containers/Messages/screens/MessageSingle/components/MessageEditFields/MessageEditFields";
import { actions as resourcesActions } from "containers/Resources/Resources.reducer";
import {
    getTemporaryRecordData,
    getTrashStateSelector,
    objectTemplateSchemaSelector,
} from "containers/Resources/Resources.selectors";
import {
    createWebhook,
    isCreateScheduleDisabled,
} from "containers/Resources/components/CommonActions/utils";
import { ResourceEditFields } from "containers/Resources/components/ResourceEditFields/ResourceEditFields";
import { OnChangeSchedulerId } from "containers/Resources/screens/ResourcesSingle/OnChangeSchedulerId";
import { CustomObjectList } from "containers/Resources/screens/ResourcesSingle/components/CustomObjectList";
import { ExecutionInputOutputValueTab } from "containers/Resources/screens/ResourcesSingle/components/ExecutionInputOutputValueTab";
import { ExecutionSchemaTab } from "containers/Resources/screens/ResourcesSingle/components/ExecutionSchemaTab";
import { getRecordOrContainerIsGitEnabled } from "containers/Resources/screens/ResourcesSingle/components/GitAction/GitAction";
import {
    getUncommittedGitLogData,
    isContainerType,
} from "containers/Resources/screens/ResourcesSingle/components/GitAction/commitActions/utils";
import { CommentsPanel } from "containers/Resources/screens/ResourcesSingle/components/CommentsPanel/CommentsPanel";
import { MetadataList } from "containers/Resources/screens/ResourcesSingle/components/MetadataList/MetadataList";
import { ObjectTemplateAttributeList } from "containers/Resources/screens/ResourcesSingle/components/ObjectTemplateAttributeList";
import { PluginActionList } from "containers/Resources/screens/ResourcesSingle/components/PluginActionList";
import RecordActionButtons from "containers/Resources/screens/ResourcesSingle/components/RecordActionButtons";
import { useDefaultObjectTemplate } from "containers/Resources/screens/ResourcesSingle/components/RecordLogList/utils";
import { ResourceWrapperContainer } from "containers/Resources/screens/ResourcesSingle/components/ResourceWrapper/ResourceWrapperContainer";
import ResourceSinglePageHeader from "containers/Resources/screens/ResourcesSingle/components/ResourcesSinglePageHeader";
import { RolePermissionList } from "containers/Resources/screens/ResourcesSingle/components/RolePermissionList";
import { CreateScheduleButton } from "containers/Resources/screens/ResourcesSingle/components/UsedByList/CreateScheduleButton";
import { UsedBySingleRecordList } from "containers/Resources/screens/ResourcesSingle/components/UsedByList/UsedByList";
import {
    customObjectUsedByListQuery,
    scheduleQuery,
    settingHoldingLockListQuery,
} from "containers/Resources/screens/ResourcesSingle/components/UsedByList/utils";
import { UserGitPublicKeyList } from "containers/Resources/screens/ResourcesSingle/components/UserGitPublicKeyList";
import { ExecutionRelatedRecordListing } from "containers/Resources/screens/ResourcesSingle/components/Visualization/DependencyVisualization/DependencyVisualization";
import {
    commentsContentType,
    commentsTabKey,
    executionRelatedRecordsContentType,
    holdingTheLockTabKey,
    inputSchemaContentType,
    metadataContentType,
    metadataTabKey,
    outputSchemaContentType,
    schedulerKey,
    usedByContentKey,
    usedBySchedulesList,
    usedByWebhooksList,
    valueAndSchema,
    valueAndSchemaContentType,
    valueAndSchemaInput,
    valueAndSchemaOutput,
    wrappersContentType,
    wrappersTabKey,
} from "containers/Resources/screens/ResourcesSingle/constants";
import { useCanCommitOnSave } from "containers/Resources/screens/ResourcesSingle/useCanCommitOnSave";
import {
    CONNECTION_TEST_ON_SAVE_FLAG,
    runTestConnectionAfterSave,
    supportsTestConnectionOnSave,
} from "containers/Resources/components/CommonActions/testConnectionAction";
import { useResourceData } from "containers/Resources/screens/ResourcesSingle/useResourceData";
import SearchTabs from "containers/Search/components/SearchTabs";
import { actions as statsActions } from "containers/Stats/Stats.reducer";
import { useWorkspaceConfigValue } from "containers/WorkspaceConfig/useWorkspaceConfigValue";
import { bootstrapUserNameSelector } from "containers/Workspaces/Workspaces.selectors";
import { storeWorkspace } from "containers/Workspaces/utils";
import { updatedDiff } from "deep-object-diff";
import {
    Bundle,
    Custom_Object,
    Execution,
    File,
    Flow,
    Message,
    Object_Template,
    Plugin,
    Record,
    Role,
    Scheduler,
    User,
    Workspace,
} from "graphql/generated/graphql";
import { YAMLException } from "js-yaml";
import { cloneDeep, isEqual } from "lodash";
import get from "lodash/get";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { withErrorBoundary } from "react-error-boundary";
import { Field, Form as FinalForm, FormSpy, useFormState } from "react-final-form";
import { Helmet } from "react-helmet";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { actions as dataStoreActions } from "utils/DataStore/DataStore.reducer";
import { RouterContext } from "utils/RouterManager/RouterManager";
import Error, { ErrorBoundary } from "utils/RouterManager/screens/Error";
import { getLocalStorageItem, getTypedLowerCase, parseJSONSafely, prefersIdInUrl } from "utils/service";
import { getIri } from "utils/utils";
import {
    formatSearchParams,
    isInPageTabSwitch,
    parseSearchParams,
} from "utils/RouterManager/searchParams";
import strings from "utils/strings";
import useLocalStorage from "utils/useLocalStorage";
import { parseYaml } from "utils/valueTransformers";
import "./ResourcesSingle.scss";
import { RecordLogList } from "./components/RecordLogList/RecordLogList";
import { RoleIdentityList } from "./components/RoleIdentityList/RoleIdentityList";
import { roleIdentityListKey } from "./components/RoleIdentityList/constants";
import { WidgetWrapper } from "./components/WidgetWrapper";
import {
    canLoadFileKey,
    changeCanCommitOnSaveMutator,
    changeScheduler,
    changeSchemaFormValues,
    checkSSPHidden,
    fetchSingleResourceData,
    getQueryCommitParams,
    getResourceDataInitialValues,
    onKeyDown,
    resetFinalForm,
    stripUntouchedWriteOnlyFields,
    submitSingleRecordForm,
    subscribeUpdates,
    useReconnectingWebsocket,
    useSecondaryLiveDataHold,
} from "./utils";
import { getCredentialsClearedByDestinationChange } from "./credentialBinding";
import { showConfirmModal } from "components/Modal/showConfirmModal";
import { cancelButtonProps } from "components/Form/components/CancelButton/CancelButton";

const UserGitPatPanel = ({
    userId,
    userData,
    disabled,
    refreshData,
}: {
    userId: string;
    userData: any;
    disabled: boolean;
    refreshData: () => void;
}) => {
    const [loading, setLoading] = useState(false);

    const createOrRotate = async () => {
        setLoading(true);
        try {
            const r = await restClient.post(`user/${userId}/git_pat`);
            const token = r.data?.token as string | undefined;
            Modal.info({
                title: "Git HTTPS Personal Access Token (PAT)",
                content: (
                    <div style={{ marginTop: 8 }}>
                        <div style={{ marginBottom: 12 }}>
                            This token is shown only once. Please copy and store it now.
                        </div>
                        <pre
                            style={{
                                padding: 12,
                                background: "rgba(0,0,0,0.04)",
                                borderRadius: 6,
                                overflowX: "auto",
                            }}
                        >
                            {token ?? "(no token returned)"}
                        </pre>
                        <div style={{ display: "flex", gap: 8 }}>
                            <Button
                                label="Copy token"
                                variation="primary"
                                onClick={async () => {
                                    if (!token) return;
                                    await navigator.clipboard.writeText(token);
                                    NotificationService.success("Token copied to clipboard");
                                }}
                            />
                        </div>
                    </div>
                ),
                okText: "Done",
            });
            refreshData();
        } catch (error) {
            NotificationService.error(
                <RestErrorNotification titleOverride="Failed to create git token" error={error as any} />
            );
        } finally {
            setLoading(false);
        }
    };

    const revoke = async () => {
        setLoading(true);
        try {
            await restClient.delete(`user/${userId}/git_pat`);
            NotificationService.success("Git token revoked");
            refreshData();
        } catch (error) {
            NotificationService.error(
                <RestErrorNotification titleOverride="Failed to revoke git token" error={error as any} />
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="page-content" style={{ marginTop: 16 }}>
            <h3 style={{ marginBottom: 8 }}>Git HTTPS token</h3>
            <div style={{ marginBottom: 12 }}>Required for cloning/pushing repositories via HTTPS.</div>

            <div style={{ marginBottom: 12 }}>
                <div>
                    <b>Has token</b>: {userData?.has_pat ? "Yes" : "No"}
                </div>
                <div>
                    <b>Created</b>:{" "}
                    {userData?.git_pat_created_at ? (
                        <TimeAgoWithDateTimeFormatter datetime={userData.git_pat_created_at} />
                    ) : (
                        "—"
                    )}
                </div>
                <div>
                    <b>Last used</b>:{" "}
                    {userData?.git_pat_last_used_at ? (
                        <TimeAgoWithDateTimeFormatter datetime={userData.git_pat_last_used_at} />
                    ) : (
                        "—"
                    )}
                </div>
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <Button
                    label="Create / rotate token"
                    variation="primary"
                    disabled={disabled}
                    loading={loading}
                    onClick={createOrRotate}
                />
                <Button
                    label="Revoke token"
                    variation="danger"
                    disabled={disabled}
                    loading={loading}
                    onClick={() =>
                        Modal.confirm({
                            title: "Revoke git HTTPS token?",
                            content:
                                "This immediately disables HTTPS cloning/pushing using the current token.",
                            okText: "Revoke",
                            cancelText: "Cancel",
                            onOk: revoke,
                            icon: null,
                        })
                    }
                />
            </div>
        </div>
    );
};

const showApiKeyToken = (token: string | undefined) => {
    Modal.info({
        title: "API key",
        content: (
            <div style={{ marginTop: 8 }}>
                <div style={{ marginBottom: 12 }}>
                    This key is shown only once. Please copy and store it now. Use it as a bearer
                    token (<code>Authorization: Bearer &lt;key&gt;</code>) or in the{" "}
                    <code>x-cloudomation-api-key</code> header.
                </div>
                <pre
                    style={{
                        padding: 12,
                        background: "rgba(0,0,0,0.04)",
                        borderRadius: 6,
                        overflowX: "auto",
                    }}
                >
                    {token ?? "(no token returned)"}
                </pre>
                <div style={{ display: "flex", gap: 8 }}>
                    <Button
                        label="Copy key"
                        variation="primary"
                        onClick={async () => {
                            if (!token) return;
                            await navigator.clipboard.writeText(token);
                            NotificationService.success("API key copied to clipboard");
                        }}
                    />
                </div>
            </div>
        ),
        okText: "Done",
    });
};

const McpConnectionInfo = ({ mcpUrl }: { mcpUrl: string }) => (
    <div style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 8 }}>
            LLM agents (Claude, Cursor, OpenAI, ...) can connect to this workspace as an MCP server
            using the Streamable HTTP transport and an API key as bearer token:
        </div>
        <pre
            style={{
                padding: 12,
                background: "rgba(0,0,0,0.04)",
                borderRadius: 6,
                overflowX: "auto",
            }}
        >
            {`URL: ${mcpUrl}\nHeader: Authorization: Bearer <your-api-key>`}
        </pre>
        <Button
            label="Copy MCP URL"
            variation="secondary"
            onClick={async () => {
                await navigator.clipboard.writeText(mcpUrl);
                NotificationService.success("MCP URL copied to clipboard");
            }}
        />
    </div>
);

const UserApiKeyPanel = ({
    userId,
    disabled,
}: {
    userId: string;
    disabled: boolean;
}) => {
    const [loading, setLoading] = useState(false);
    const [keys, setKeys] = useState<any[]>([]);
    const [createOpen, setCreateOpen] = useState(false);
    const [newName, setNewName] = useState("");

    const mcpUrl = `${window.location.origin}/api/latest/mcp`;

    const fetchKeys = useCallback(async () => {
        if (!userId) return;
        setLoading(true);
        try {
            const r = await restClient.get("api_key", {
                params: {
                    filter: { and: [{ field: "user_id", op: "eq", value: userId }] },
                    limit: 100,
                    order: "+created_at",
                },
            });
            const data = r.data;
            setKeys(
                Array.isArray(data) ? data : Array.isArray(data?.api_key_list) ? data.api_key_list : []
            );
        } catch (error) {
            NotificationService.error(
                <RestErrorNotification
                    titleOverride="Failed to load API keys"
                    error={error as any}
                />
            );
        } finally {
            setLoading(false);
        }
    }, [userId]);

    useEffect(() => {
        fetchKeys();
    }, [fetchKeys]);

    const createKey = async () => {
        if (!newName.trim()) {
            NotificationService.error("Please enter a name for the API key");
            return;
        }
        setLoading(true);
        try {
            const r = await restClient.post("api_key", {
                name: newName.trim(),
                user_id: userId,
            });
            setCreateOpen(false);
            setNewName("");
            showApiKeyToken(r.data?.token);
            fetchKeys();
        } catch (error) {
            NotificationService.error(
                <RestErrorNotification
                    titleOverride="Failed to create API key"
                    error={error as any}
                />
            );
        } finally {
            setLoading(false);
        }
    };

    const revokeKey = async (id: string) => {
        setLoading(true);
        try {
            await restClient.delete(`api_key/${id}`);
            NotificationService.success("API key revoked");
            fetchKeys();
        } catch (error) {
            NotificationService.error(
                <RestErrorNotification
                    titleOverride="Failed to revoke API key"
                    error={error as any}
                />
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="page-content" style={{ marginTop: 16 }}>
            <h3 style={{ marginBottom: 8 }}>API keys (MCP / programmatic access)</h3>
            <div style={{ marginBottom: 12 }}>
                API keys let LLM agents and scripts authenticate to the Cloudomation API and MCP
                server. A key inherits this user's permissions, so scope access via the user's
                roles.
            </div>

            <McpConnectionInfo mcpUrl={mcpUrl} />

            {keys.length > 0 ? (
                <table style={{ width: "100%", marginBottom: 12, borderCollapse: "collapse" }}>
                    <thead>
                        <tr style={{ textAlign: "left" }}>
                            <th style={{ padding: "4px 8px" }}>Name</th>
                            <th style={{ padding: "4px 8px" }}>Prefix</th>
                            <th style={{ padding: "4px 8px" }}>Created</th>
                            <th style={{ padding: "4px 8px" }}>Last used</th>
                            <th style={{ padding: "4px 8px" }}>Expires</th>
                            <th style={{ padding: "4px 8px" }}>Enabled</th>
                            <th style={{ padding: "4px 8px" }} />
                        </tr>
                    </thead>
                    <tbody>
                        {keys.map((key) => (
                            <tr key={key.id} style={{ borderTop: "1px solid rgba(0,0,0,0.08)" }}>
                                <td style={{ padding: "4px 8px" }}>{key.name}</td>
                                <td style={{ padding: "4px 8px" }}>
                                    <code>{key.key_prefix}</code>
                                </td>
                                <td style={{ padding: "4px 8px" }}>
                                    {key.created_at ? (
                                        <TimeAgoWithDateTimeFormatter datetime={key.created_at} />
                                    ) : (
                                        "—"
                                    )}
                                </td>
                                <td style={{ padding: "4px 8px" }}>
                                    {key.last_used_at ? (
                                        <TimeAgoWithDateTimeFormatter datetime={key.last_used_at} />
                                    ) : (
                                        "Never"
                                    )}
                                </td>
                                <td style={{ padding: "4px 8px" }}>
                                    {key.expires_at ? (
                                        <TimeAgoWithDateTimeFormatter datetime={key.expires_at} />
                                    ) : (
                                        "Never"
                                    )}
                                </td>
                                <td style={{ padding: "4px 8px" }}>
                                    {key.is_enabled ? "Yes" : "No"}
                                </td>
                                <td style={{ padding: "4px 8px" }}>
                                    <Button
                                        label="Revoke"
                                        variation="danger"
                                        disabled={disabled}
                                        onClick={() =>
                                            Modal.confirm({
                                                title: "Revoke API key?",
                                                content:
                                                    "This immediately disables any client using this key.",
                                                okText: "Revoke",
                                                cancelText: "Cancel",
                                                onOk: () => revokeKey(key.id),
                                                icon: null,
                                            })
                                        }
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            ) : (
                <div style={{ marginBottom: 12 }}>No API keys yet.</div>
            )}

            <Button
                label="Create API key"
                variation="primary"
                disabled={disabled}
                loading={loading}
                onClick={() => setCreateOpen(true)}
            />

            <Modal
                title="Create API key"
                visible={createOpen}
                onOk={createKey}
                onCancel={() => {
                    setCreateOpen(false);
                    setNewName("");
                }}
                okText="Create"
                confirmLoading={loading}
            >
                <div style={{ marginBottom: 8 }}>Give the key a descriptive name:</div>
                <Input
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="e.g. Claude agent"
                    onPressEnter={createKey}
                />
            </Modal>
        </div>
    );
};

export const formId = "resourcesSingleForm";

export const customObjectListKey = "customObjectList";

// localStorage tableSettings keys for the object_template attribute list and the
// role permission list, so page size / sort order persist across navigation and
// reload (parity with the CO list and top-level record lists).
export const objectTemplateAttributeListKey = "objectTemplateAttributeList";
export const rolePermissionListKey = "rolePermissionList";

export const initialQuery = {
    limit: 10 as 10,
    order: "+name",
    offset: 0,
};

// The object_template attributes table defaults to sort_order ascending so the
// list opens in the reorderable state (the up/down move controls are only
// coherent while rows are shown in their persisted sort_order). Sorting by any
// other column disables reorder until the user sorts by sort_order again.
export const attributeInitialQuery = {
    limit: 10 as 10,
    order: "+sort_order",
    offset: 0,
};

interface TrashStateExtended {
    pending_restore_identity_id: string | null;
    deleted_at: string | null;
    pending_delete_identity_id: string | null;
    pending_trash_identity_id: string | null;
}
interface TrashStateGone {
    gone: boolean;
}
type TrashState = {
    id: string;
} & (TrashStateExtended | TrashStateGone);
const isDashboardSection = (item: DashboardItemConfig): item is DashboardSectionConfig => {
    return (item as any)?.component === "CollapsibleSection" && "items" in (item as any);
};

const getDashboardSectionStorageKey = (
    resourceName: string,
    recordId: string | undefined,
    section: DashboardSectionConfig
) => {
    const storageKey = section.storageKey ?? section.uid;
    const recordPart = recordId ?? "global";
    return `dashboard:${resourceName}:${recordPart}:${storageKey}`;
};

const readDashboardSectionExpanded = (
    resourceName: string,
    recordId: string | undefined,
    section: DashboardSectionConfig
) => {
    const localStorageKey = getDashboardSectionStorageKey(resourceName, recordId, section);
    return parseJSONSafely(getLocalStorageItem(localStorageKey), section.defaultExpanded ?? true);
};

const DashboardCollapsibleSection = ({
    section,
    resourceName,
    recordId,
    resourceData,
    onExpandedChange,
}: {
    section: DashboardSectionConfig;
    resourceName: string;
    recordId?: string;
    resourceData: Record | null;
    onExpandedChange?: (expanded: boolean) => void;
}) => {
    const localStorageKey = getDashboardSectionStorageKey(resourceName, recordId, section);
    const [isExpanded, setExpanded] = useLocalStorage(
        localStorageKey,
        section.defaultExpanded ?? true
    );

    // WidgetWrapper requires a string recordId; keep dashboards stable when recordId isn't present.
    const widgetRecordId = recordId ?? "global";

    useEffect(() => {
        onExpandedChange?.(!!isExpanded);
        // Sync parent once on mount; toggles are reported via handleExpandedChange.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleExpandedChange = (expanded: boolean) => {
        setExpanded(expanded);
        onExpandedChange?.(expanded);
    };

    return (
        <div key={section.uid} className={`${section.className ?? "w-full"} pb-4`}>
            <Accordion
                title={section.title}
                isExpanded={!!isExpanded}
                onExpandedChange={(v) => handleExpandedChange(!!v)}
                disableDetailsPadding
            >
                <div className="flex flex-wrap">
                    {section.items?.map((child: DashboardWidgetConfig) => (
                        <WidgetWrapper
                            dashboardItem={child}
                            recordId={widgetRecordId}
                            resourceData={resourceData}
                            key={child.uid}
                        />
                    ))}
                </div>
            </Accordion>
        </div>
    );
};

/**
 * Watches only the input/output schema-tab dirty flags and reports changes to
 * the parent. Lives inside <FinalForm> and subscribes via useFormState so the
 * main form render prop no longer has to read dirtyFields during render (which
 * previously triggered a setState-in-render and coupled the whole tree to the
 * form's dirty state).
 */
const SchemaTabDirtyTracker = ({ onChange }: { onChange: (hasChanges: boolean) => void }) => {
    const { dirtyFields } = useFormState({ subscription: { dirtyFields: true } });
    const hasChanges = !!(
        dirtyFields[valueAndSchemaInput] || dirtyFields[valueAndSchemaOutput]
    );
    useEffect(() => {
        onChange(hasChanges);
    }, [hasChanges, onChange]);
    return null;
};

/**
 * ResourceSingle
 *
 * Main component for displaying and editing a single resource.
 * Handles fetching, form state, and rendering dynamic layouts based on resource type.
 * Integrates with Redux for state management and supports various resource-specific features.
 */
const ResourceSingle = () => {
    // Redux dispatch and React Router history
    const dispatch = useDispatch();
    const history = useHistory();
    const {
        resourceData,
        isFetching,
        userData,
        isReadOnly,
        isDisabled,
        iri,
        resourceName,
        uid,
        recordId,
        hasNameQueryParam,
        resourceConfig,
        isWorkspace,
    } = useResourceData();
    const trashState: TrashState[] = useSelector(getTrashStateSelector);

    const dashboardCollapsibleSection = useMemo(
        () => resourceConfig?.dashboard?.find(isDashboardSection),
        [resourceConfig?.dashboard]
    );
    const [isDashboardPanelExpanded, setIsDashboardPanelExpanded] = useState(true);

    useEffect(() => {
        if (!dashboardCollapsibleSection) {
            setIsDashboardPanelExpanded(true);
            return;
        }
        setIsDashboardPanelExpanded(
            readDashboardSectionExpanded(
                resourceConfig.resourceName,
                recordId,
                dashboardCollapsibleSection
            )
        );
    }, [dashboardCollapsibleSection, resourceConfig.resourceName, recordId]);

    const bootstrapUserName = useSelector(bootstrapUserNameSelector);

    const isExecution = resourceConfig.resourceName === executionConfig.resourceName;
    const isExecutionRunning = (resourceData as Execution)?.ended_at === null;
    const defaultTab = isExecution
        ? isExecutionRunning
            ? scriptTabKey
            : outputTabKey
        : resourceConfig.defaultActiveTab;
    const [activeTab, setActiveTab] = useState(defaultTab || "all");
    const writeOnlyFieldsWithData = getWriteOnlyFieldsWithData(
        resourceName,
        resourceData as Record
    );
    const isFieldDisabled = isReadOnly || isDisabled;
    /**
     * Controls the loading state of the component.
     * Set to true initially and false after component mount.
     *
     * @type {boolean}
     * @default true
     */
    const [isLoading, setLoading] = useState(true);

    /**
     * Query parameters for fetching role identity list.
     * Used for pagination and filtering of identity data.
     *
     * @type {Object}
     * @property {number} limit - Number of records to fetch per page
     * @property {number} offset - Number of records to skip
     */
    const identityInitialQuery = {
        limit: 10,
        offset: 0,
    };

    /**
     * State for managing role identity list query parameters.
     *
     * @type {Object}
     * @property {number} limit - Number of records to fetch per page
     * @property {number} offset - Number of records to skip
     */
    const [identityQuery, setIdentityQuery] = useState({
        ...identityInitialQuery,
        limit: getTableSettings(roleIdentityListKey, "limit") || identityInitialQuery.limit,
    });
    const updateIdentityQuery = useCallback(
        (changedQuery) => setIdentityQuery({ ...identityQuery, ...changedQuery }),
        [identityQuery]
    );

    const rolePermissionInitialQuery = {
        limit: 10,
        offset: 0,
        order: "+operation",
    };
    const [rolePermissionQuery, setRolePermissionQuery] = useState(
        getInitialQueryWithTableSettings(rolePermissionListKey, rolePermissionInitialQuery)
    );
    const updateRolePermissionQuery = useCallback(
        (changedQuery) => setRolePermissionQuery({ ...rolePermissionQuery, ...changedQuery }),
        [rolePermissionQuery]
    );

    const [customObjectListQuery, setCustomObjectListQuery] = useState({
        ...getInitialQueryWithTableSettings(`customObjectList-${recordId}`, initialQuery),
        // Trash toggle (ShowDeletedFilter in CustomObjectList): hide trashed COs
        // by default, keep normal rows. Flipped per user choice.
        allow_deleted: false,
        allow_normal: true,
    });

    const updateCustomObjectListQuery = useCallback(
        (queryChange) => {
            setCustomObjectListQuery({ ...customObjectListQuery, ...queryChange });
        },
        [customObjectListQuery]
    );

    const [attributeListQuery, setAttributeListQuery] = useState(
        getInitialQueryWithTableSettings(objectTemplateAttributeListKey, attributeInitialQuery)
    );

    const updateAttributeListQuery = useCallback(
        (queryChange) => {
            setAttributeListQuery({ ...attributeListQuery, ...queryChange });
        },
        [attributeListQuery]
    );

    const identityVariables = useMemo(
        () =>
            resourceConfig.resourceName === roleConfig.resourceName
                ? {
                    rolePermissionLimit: rolePermissionQuery.limit,
                    rolePermissionOffset: rolePermissionQuery.offset,
                    rolePermissionOrder: rolePermissionQuery.order,
                }
                : resourceConfig.resourceName === objectTemplateConfig.resourceName
                    ? {
                        customObjectLimit: customObjectListQuery.limit,
                        customObjectOrder: customObjectListQuery.order,
                        customObjectOffset: customObjectListQuery.offset,
                        // Trash toggle for the CO list. Defaults hide trashed COs
                        // (allow_deleted:false) while keeping normal rows
                        // (allow_normal:true); the ShowDeletedFilter in
                        // CustomObjectList flips these per user choice.
                        customObjectAllowDeleted: customObjectListQuery.allow_deleted ?? false,
                        customObjectAllowNormal: customObjectListQuery.allow_normal ?? true,
                        attributeLimit: attributeListQuery.limit,
                        attributeOrder: attributeListQuery.order,
                        attributeOffset: attributeListQuery.offset,
                    }
                    : {},
        [
            attributeListQuery.limit,
            attributeListQuery.offset,
            attributeListQuery.order,
            customObjectListQuery.limit,
            customObjectListQuery.offset,
            customObjectListQuery.order,
            customObjectListQuery.allow_deleted,
            customObjectListQuery.allow_normal,
            resourceConfig.resourceName,
            rolePermissionQuery.limit,
            rolePermissionQuery.offset,
            rolePermissionQuery.order,
        ]
    );

    /**
     * Stores the save functions of nested forms to enable saving nested form data
     * when the parent form is saved.
     *
     * @type {Object.<string, () => Promise<any>>}
     * @description Maps nested form IDs to their respective save functions
     */
    const [nestedFormSaveData, setNestedFormSaveData] = useState<{
        [nestedFormId: string]: () => Promise<any>;
    }>({});

    /**
     * Controls the visibility of the record popover for creating/editing subrecords or relationships.
     *
     * @type {boolean}
     * @default false
     */
    const [isRecordPopoverOpen, setRecordPopoverOpen] = useState(false);

    /**
     * Stores the data of the subrecord to be displayed in the popover.
     * Contains resource information for creating/editing plugin actions, role permissions, etc.
     *
     * @type {any}
     * @description Object containing resourceName, recordId, isReadOnly, isDisabled, etc.
     */
    const [popoverRecord, setPopoverRecord] = useState<any>();

    /**
     * Handles the click event for creating/editing subrecord or relationship (plugin_action, role_permission etc)
     * @param e - The click event
     * @param id - The id of the subrecord or relationship, when creating it is undefined
     * @param resource_type - The type of the subrecord or relationship
     * @returns {void}
     */
    const onResourceClick = ({ e, id, resource_type }) => {
        if (e) e.preventDefault();

        const selectedData = {
            resourceName: resource_type,
            recordId: id,
            isReadOnly,
            isDisabled,
            parentResourceType: resourceName,
            parentResourceId: resourceData?.id,
            canTrackInGitParent: resourceData?.track_in_git,
            isGitEnabled,
        };

        setPopoverRecord(selectedData);
        setRecordPopoverOpen(true);
    };

    const isOwnUser = recordId === userData?.identity_id;

    const isUserType = resourceName === userConfig.resourceName;
    const isSSPHidden = checkSSPHidden();

    const isFileType = resourceName === fileConfig.resourceName;

    const alwaysLoadFileLimit = useWorkspaceConfigValue("FILE_SIZE_BYTES_ALWAYS_LOAD_LIMIT");
    const optionalLoadFileLimit = useWorkspaceConfigValue("FILE_SIZE_BYTES_OPTIONAL_LOAD_LIMIT");
    // Automatic post-save connectivity test for workspace-configuration records
    // (vault_config/ldap_config/devolutions_config). Default-enabled: only an
    // explicit `false` disables it. See operator-task-workspace-config-connectivity-test-action.
    const connectionTestOnSaveEnabled =
        useWorkspaceConfigValue(CONNECTION_TEST_ON_SAVE_FLAG) !== false;
    const contentSizeBytes = useMemo(
        () => (resourceData as File)?.content_size_bytes,
        [resourceData]
    );
    // Server-detected FILE MIME type (backend resources/file.py content_type column).
    // Not yet in the generated graphql types -> narrow cast.
    // (operator-task-fe-file-mimetype-adaptive-display.)
    const fileContentType = useMemo(
        () => (resourceData as { content_type?: string } | undefined)?.content_type,
        [resourceData]
    );
    const fileDisplay = useMemo(() => resolveFileDisplay(fileContentType), [fileContentType]);
    const isFileContentAlwaysHidden = useMemo(
        () => isFileType && optionalLoadFileLimit && contentSizeBytes > optionalLoadFileLimit,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isFileType, optionalLoadFileLimit, contentSizeBytes]
    );

    /**
     * Controls whether file content should be displayed.
     * Used for large files that are optionally loaded.
     *
     * @type {boolean}
     * @default false
     */
    const [showFileContent, setShowFileContent] = useState(false);
    const canLoadFileContent = useMemo(
        () =>
            (isFileType && alwaysLoadFileLimit && contentSizeBytes <= alwaysLoadFileLimit) ||
            showFileContent,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [alwaysLoadFileLimit, isFileType, contentSizeBytes, showFileContent]
    );

    /*
    dispatch is used to store the canLoadFileContent in the dataStore
    it is used to check if the file content should be loaded from the backend
    */
    useEffect(() => {
        if (isFileType) {
            dispatch({
                type: dataStoreActions.STORE_DATA,
                key: canLoadFileKey(iri),
                data: canLoadFileContent,
            });
        }
    }, [canLoadFileContent, dispatch, iri, isFileType]);

    /**
     * Tracks whether the form has new data from the backend.
     * Used to show dirty form warnings when backend data changes.
     *
     * @type {boolean}
     * @default false
     */
    const [hasNewData, setHasNewData] = useState(false);

    /**
     * Bumped by onRefresh (for schema-form records) to force additionalFieldProps to
     * recompute after form.restart(), so its isDirty flag re-reflects the now-pristine
     * form and drives the SchemaForm editors to discard local edits and show server state.
     */
    const [refreshNonce, setRefreshNonce] = useState(0);

    /**
     * Bumped on every manual Refresh to re-fetch the record's comment list.
     * The comment list is a separate GraphQL query (dataStore slice
     * recordCommentList-<id>) that fetches on mount/tab-open/mutation only, so it
     * does not participate in the record's own re-fetch. Without this signal the
     * comments-tab badge count updates on refresh (it rides the record body) while
     * the rendered list stays stale. Passed to CommentsPanel and added to its fetch
     * effect deps so both update together. See
     * operator-task-fe-record-refresh-stale-comments.
     */
    const [commentsRefreshNonce, setCommentsRefreshNonce] = useState(0);

    /**
     * True while a user-initiated manual Refresh (the header Refresh button) is
     * re-fetching the record from the backend. Drives the button's disabled/loading
     * state. See onManualRefresh + operator-task-fe-single-record-screen-refresh-button.
     */
    const [isManualRefreshing, setIsManualRefreshing] = useState(false);

    /**
     * Set after a "Migrate deprecations" apply succeeds. The migrate saga mutates
     * the record's script/value ON THE SERVER and merges the migrated record into
     * the store, but the open editors keep their pre-migration buffer until a
     * remount. This flag defers the editor re-seed to an effect keyed on
     * storeRecordVersion so it runs once the migrated record has propagated into
     * initialValues. See
     * operator-task-fe-migrate-deprecations-code-editor-no-refresh.
     */
    const [pendingMigrateReseed, setPendingMigrateReseed] = useState(false);

    /**
     * The server record_version currently in the store. The migrate-reseed effect
     * keys on it so it re-runs once the migrated record has propagated into
     * initialValues. See operator-task-fe-migrate-deprecations-code-editor-no-refresh.
     */
    const storeRecordVersion: string | undefined = resourceData?.record_version;
    const resetDirty = useCallback(() => {
        setHasNewData(false);
    }, []);

    const setDirtyForm = useCallback(() => setHasNewData(true), []);

    const onMigrateDeprecationsApplied = useCallback(() => setPendingMigrateReseed(true), []);

    /**
     * Per-field live-conflict detector for websocket pushes.
     *
     * Stefan's rule (operator-question-fe-record-modified-guard-fix-direction): only
     * raise the "record has been modified" warning when the backend changes a field the
     * user has ALSO edited locally. For fields the user hasn't touched (pristine), the
     * new server value is adopted silently by react-final-form's keepDirtyOnReinitialize
     * with no warning. A websocket RECONNECT (e.g. after a backend deploy) re-pushes the
     * record's UNCHANGED state -> no field changed -> no false-positive warning.
     *
     * Held in a ref (reassigned each render) so the mount-time subscription callback
     * always runs the latest closure without needing to resubscribe. The callback fires
     * synchronously right after the STORE_RESOURCE_DATA dispatch but before React
     * re-renders, so formRef still holds the OLD baseline to compare against.
     */
    const pushConflictRef = useRef<(incomingData: any) => boolean>(() => false);

    const isBundle = resourceConfig.resourceName === bundleConfig.resourceName;

    const isCustomObject = resourceConfig.resourceName === customObjectConfig.resourceName;
    const isConnector = resourceConfig.resourceName === connectorConfig.resourceName;
    const isFlow = resourceConfig.resourceName === flowConfig.resourceName;
    const isScheduler = resourceConfig.resourceName === schedulerConfig.resourceName;
    const isSchedule = resourceConfig.resourceName === scheduleConfig.resourceName;
    const isWrapper = resourceConfig.resourceName === wrapperConfig.resourceName;
    const hasRecordSchemaForm =
        isCustomObject || isConnector || isFlow || isScheduler || isWrapper || isSchedule;
    /**
     * Fetches resource data from the backend and updates the Redux store.
     * Also checks for changes to set the dirty form state.
     * @returns {Promise<any>} The fetched resource data or error.
     */
    const fetchResourceData = useCallback((override?: { id?: string } | unknown) => {
        // A rename leaves the by-name URL/closure pointing at the OLD name for one
        // render, so an automatic refetch fired in the same tick as the save (e.g.
        // an action button that saves-then-runs, then refreshes) would query the
        // stale name and 404 as "resource not found". When the caller has the
        // freshly-saved record, refetch it by its stable id instead. Guard against
        // being invoked as a plain callback/event handler (override without an id).
        const overrideId =
            override && typeof override === "object" && !Array.isArray(override)
                ? (override as { id?: string }).id
                : undefined;
        const targetBy = overrideId ? "id" : hasNameQueryParam ? "name" : "id";
        const targetRef = overrideId ?? uid;
        const targetIri = overrideId ? getIri(resourceName, overrideId) : iri;
        const setOpened = isSubrecordOrRelationshipType(resourceName)
            ? null
            : resourceConfig.resourceClass === "resource" || resourceName === "execution";
        const additionalVariables = { set_opened: setOpened, ...identityVariables };

        return fetchSingleResourceData({
            dispatch,
            resourceName,
            iri: targetIri,
            recordId: targetRef,
            additionalVariables,
            by: targetBy,
            additionalQueryFields: canLoadFileContent ? "content" : "",
        })
            .then((response) => {
                if (resourceName === "user" && isOwnUser) {
                    dispatch({
                        type: authActions.UPDATE_USER_ICON,
                        response: (response as User)?.icon,
                    });
                }
                if (resourceName === "workspace") {
                    const workspaceResponse = response as Workspace;
                    storeWorkspace({ response: workspaceResponse, dispatch });
                }
                const formValuesCopy = cloneDeep(formRef.current?.getState().values);
                delete formValuesCopy["canCommitOnSave"];
                const equal = isEqual(formValuesCopy, response);
                if (!equal && (hasRecordSchemaForm || resourceName === bundleConfig.resourceName)) {
                    setDirtyForm();
                }
                setHasFetchedInitialValues(true);
            })
            .catch(() => {
                setHasFetchedInitialValues(true);
            });
    }, [
        canLoadFileContent,
        dispatch,
        hasNameQueryParam,
        hasRecordSchemaForm,
        identityVariables,
        iri,
        isOwnUser,
        resourceConfig.resourceClass,
        resourceName,
        setDirtyForm,
        uid,
    ]);

    const formRef = useRef<any>(null);

    // isNotFound is used to display a not found screen when the websocket returns null (custom_object, execution)
    const [isNotFound, setNotFound] = useState(false);
    const showErrorScreen = useCallback(() => setNotFound(true), []);

    const secondaryLiveDataHold = useSecondaryLiveDataHold();

    const { getSocket, deleteSocket } = useReconnectingWebsocket();

    useEffect(() => {
        // Do not open the live-data subscription until the record id is resolved.
        // For by-name deep links (?name=…) recordId is known only after
        // resourceData has loaded; issuing the custom_object($id: String!)
        // subscription earlier sends it with empty variables ({}), which the
        // backend rejects on the required $id variable and the FE surfaces as a
        // spurious "Could not load live data" toast. recordId is in the dep
        // array so the subscription opens exactly once, when the id becomes known.
        if (!recordId) {
            return;
        }
        const customObjectSubscription = `subscription custom_object($id: String!) {
            custom_object(id: $id) {
                ${resourceConfig.graphql.resourceBody}
            }
        }
        `;
        const cleanup =
            isCustomObject && !secondaryLiveDataHold
                ? subscribeUpdates({
                    subscription: customObjectSubscription,
                    recordId,
                    dispatch,
                    recordName: resourceName,
                    iri,
                    callback: (data) => {
                        if (isCustomObject && pushConflictRef.current(data)) {
                            setDirtyForm();
                        }
                    },
                    showErrorScreen,
                    getSocket,
                    deleteSocket,
                })
                : null;
        return () => {
            cleanup?.();
        };
        // TODO (low) evaluate alternative fixes, exhaustive adding of deps seems unclean, esp. with additional noise of useWrapper etc.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [iri, recordId, secondaryLiveDataHold]);

    const bundleRepositoryData = useMemo(
        () => (resourceData as Bundle)?.bundle_repository_id_bundle_repository,
        [resourceData]
    );
    useEffect(() => {
        if (bundleRepositoryData?.id) {
            const { name, id } = bundleRepositoryData;

            fetchSingleBundleRepository({
                dispatch,
                bundleRepositoryId: id,
                bundleRepositoryName: name,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bundleRepositoryData?.name]);

    const objectTemplateList = useSelector(objectTemplateSchemaSelector);

    /**
     * filteredFormStructure is used to filter the form structure based on the resource type.
     * The fields are displayed in tabs instead of the ResourceEditFields component
     */
    const filteredFormStructure = useMemo(
        () =>
            resourceConfig.form?.filter((field) => {
                if (isConnector && field.name === valueAndSchema) {
                    return false;
                } else
                    return (
                        field.name !== valueAndSchemaInput && field.name !== valueAndSchemaOutput
                    );
            }),
        [resourceConfig.form, isConnector]
    );

    const schemaFormSchema = useContext(RouterContext).filteredSchemaFormSchema;

    useEffect(() => {
        // Clear the initial loading state only once there is an id to fetch.
        // For the workspace route the id is not known synchronously — it
        // arrives via auth/check -> user-data (redux) shortly after mount.
        // Clearing on mount (regardless of `uid`) would momentarily flash the
        // not-found Error screen while the workspace id is still undefined.
        // Non-workspace routes get their id synchronously from the URL, so this
        // clears immediately; when there is genuinely no id (bad link), the
        // `!isWorkspace` branch still clears it and the Error screen shows.
        if (uid || !isWorkspace) {
            setLoading(false);
        }
    }, [uid, isWorkspace]);

    useEffect(() => {
        if (!uid) return;
        fetchResourceData();
    }, [uid, identityVariables, canLoadFileContent, fetchResourceData]);

    useEffect(() => {
        if (resourceData) {
            const currentRecordTrashState = trashState.find((state) => state.id === recordId);
            if (currentRecordTrashState) {
                if ("gone" in currentRecordTrashState) {
                    showErrorScreen();
                } else {
                    dispatch({
                        type: resourcesActions.STORE_RESOURCE_DATA_WITH_PARTIAL_UPDATE,
                        iri,
                        response: currentRecordTrashState,
                    });
                }
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trashState, resourceName, recordId, dispatch, iri]);

    const fetchStats = (key, params) => {
        new Promise((resolve, reject) => {
            dispatch({
                type: statsActions.START_FETCHING_STAT,
                promise: { resolve, reject },
                endpoint: "/graphql",
                iri: key,
                params,
            });
        }).catch((error) => console.error(error));
    };

    useEffect(() => {
        if (!resourceConfig?.dashboardQueries) return;
        // Container dashboard stats are scoped by recordId (e.g. project_id).
        // On cold, name-based navigation recordId is briefly undefined until the
        // record loads; firing then would send an unscoped query and the backend
        // returns workspace-wide counts (the "empty project shows workspace
        // counts" bug). Skip until the scope resolves; the effect re-fires on
        // recordId. Workspace dashboards are legitimately unscoped.
        if (!recordId && !isWorkspace) return;
        Object.entries(resourceConfig?.dashboardQueries).forEach(([key, params]) => {
            fetchStats(key, params(recordId));
        });
        // TODO (low) evaluate alternative fixes, exhaustive adding of deps seems unclean, esp. with additional noise of useWrapper etc.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recordId]);

    const subscribeRecordExtra = (key, params) => {
        const typedWindow: any = window;
        const ws_host = typedWindow.REACT_APP_WEBSOCKET_API || `wss://${window.location.host}`;
        const ws_url = `${ws_host}${typedWindow.REACT_APP_WEBSOCKET_API_NAMESPACE}/graphql`;
        // Send the subscription in the first websocket message; keep GraphQL out
        // of the URL. Cache per subscription key (client-side only) so distinct
        // dashboard subscriptions keep distinct sockets despite the shared URL.
        const socketUrl = `${ws_url}?subscription_message=true`;
        const cacheKey = `${ws_url}#${key}#${recordId}`;
        const socket = getSocket(socketUrl, cacheKey);

        socket.addEventListener("open", () => {
            socket.send(JSON.stringify(params));
        });
        socket.addEventListener("message", (event) => {
            dispatch({
                type: statsActions.STORE_STAT_DATA,
                iri: key,
                response: JSON.parse(event.data),
            });
        });
        socket.addEventListener("error", (event) => {
            console.error("Websocket encountered an error ", event);
        });
        return () => {
            deleteSocket(cacheKey);
        };
    };

    useEffect(() => {
        if (
            !resourceConfig?.dashboardSubscriptions ||
            secondaryLiveDataHold ||
            !isDashboardPanelExpanded ||
            // same scope guard as the stats query: a container-scoped live list
            // (project_id eq recordId) must not subscribe with an unresolved scope.
            (!recordId && !isWorkspace)
        ) {
            return;
        }

        const cleanups = Object.entries(resourceConfig?.dashboardSubscriptions).map(
            ([key, params]) => subscribeRecordExtra(key, params(recordId))
        );
        return () => {
            cleanups.forEach((cleanup) => {
                cleanup();
            });
        };
        // TODO (low) evaluate alternative fixes, exhaustive adding of deps seems unclean, esp. with additional noise of useWrapper etc.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recordId, secondaryLiveDataHold, isDashboardPanelExpanded]);

    /*
    fetches scheduler list
    is used in the "create schedule" button on the flow record screen
    */
    useEffect(() => {
        if (recordId && isFlow) {
            new Promise(() => {
                dispatch({
                    type: dataStoreActions.START_FETCH_DATA,
                    key: schedulerKey,
                    params: {
                        query: `query {
                                scheduler_plain_list(limit: 100, offset: 0, order: "+name"){
                                id
                                name
                            }
                                }`,
                        allow_normal: true,
                        allow_deleted: false,
                    },
                });
            });
        }
    }, [dispatch, isFlow, recordId]);

    /**
     * Handles form submission for message responses.
     * Is called when message record is displayed inside execution record
     * Dispatches an update to the backend if the message is not already submitted.
     * @param formData - The form data to submit
     * @param form - The form instance
     * @returns {Promise<any>} The result of the update action
     */
    const submitMessageResponse = (formData, form) => {
        // if message was submitted, do not dispatch update
        if (
            (resourceName === "message" && (resourceData as Message)?.is_submitted) ||
            (resourceName === "execution" &&
                (resourceData as Execution)?.message_id_message?.is_submitted)
        ) {
            return Promise.resolve();
        }
        return new Promise((resolve, reject) => {
            const variables: { [key: string]: any } = {};
            variables.id =
                resourceName === "execution" ? (resourceData as Execution)?.message_id : recordId;
            if (formData?.is_submitted) {
                variables.is_submitted = formData?.is_submitted;
                form.change("is_submitted", undefined);
                delete formData.is_submitted;
            }
            if (Object.keys(formData)?.length > 0) {
                variables.response = {
                    ...formData,
                    cloudomation_fe_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                };
            }

            dispatch({
                type: resourcesActions.START_UPDATE_RESOURCE,
                promise: { resolve, reject },
                resourceName: "message",
                variables: variables,
                iri: getIri("message", variables.id),
            });
        });
    };
    const isGitEnabled = resourceData
        ? getRecordOrContainerIsGitEnabled(
            getTypedLowerCase(resourceData?.record_type),
            resourceData
        )
        : undefined;
    const { canCommitOnSaveDefaultValue } = useCanCommitOnSave(isGitEnabled);

    /**
     * Handles form submission for general resource records.
     * Prepares commit parameters and submits the form data.
     * @param formData - The form data to submit
     * @param form - The form instance
     * @returns {Promise<any>} The result of the update action
     */
    const submitRecordForm = (formData, form) => {
        const commitParams = getQueryCommitParams({
            formData,
            form,
            canCommitOnSaveDefaultValue,
            canTrackInGit: resourceData?.track_in_git,
        });
        return submitSingleRecordForm({
            formData,
            resourceConfig,
            dispatch,
            resourceName,
            additionalQueryFields: canLoadFileContent ? "content" : "",
            additionalParams: commitParams,
            iri,
        })?.then((data) => {
            resetFinalForm(form);
            // Keep the by-name URL in sync after a rename. The single-record
            // screen addresses the record via the ?name= URL param
            // (getRecordUID/useResourceData); the save persists by id, but
            // nothing rewrites the URL. Without this, a later fetch-by-name — the
            // automatic refetch after an action such as Run, or a browser Back to
            // this page — queries the OLD, now-nonexistent name and 404s as
            // "resource not found". Re-key the cached record to the new iri first
            // so the re-render finds it (no not-found flash), then replace the URL.
            const savedRecord = data as { name?: string; id?: string } | undefined;
            if (hasNameQueryParam && savedRecord?.name && savedRecord.name !== uid) {
                dispatch({
                    type: resourcesActions.STORE_RESOURCE_DATA,
                    iri: getIri(resourceName, savedRecord.name),
                    response: savedRecord,
                });
                const nextParams = parseSearchParams(window.location.search || "");
                nextParams.name = savedRecord.name;
                history.replace({ search: formatSearchParams(nextParams) });
            }
            // After a successful frontend save/enable of a workspace-configuration
            // record, automatically run its connectivity+auth test so a bad
            // credential or unreachable server surfaces immediately (gated on the
            // CONNECTION_TEST_ON_SAVE_ENABLED workspace flag). Fire-and-forget:
            // it never rejects and must not disturb the save's own promise chain.
            if (
                connectionTestOnSaveEnabled &&
                supportsTestConnectionOnSave(resourceName)
            ) {
                runTestConnectionAfterSave(
                    resourceName,
                    (data as { id?: string } | undefined)?.id ?? resourceData?.id
                );
            }
            return data;
        });
    };

    const createRelationshipRecord = (resourceName) => {
        return Promise.resolve(
            onResourceClick({
                e: undefined,
                id: undefined,
                resource_type: resourceName,
            })
        ).catch(console.log);
    };

    const createPluginAction = () => createRelationshipRecord("plugin_action");
    const createRolePermission = () => createRelationshipRecord("role_permission");
    const createUserGitPublicKey = () => createRelationshipRecord("user_git_public_key");
    const createObjectTemplateAttribute = () =>
        createRelationshipRecord("object_template_attribute");

    const messageBody =
        resourceConfig?.resourceName === "execution"
            ? (resourceData as Execution)?.message_id_message?.body
            : (resourceData as Message)?.body;
    const messageResponseValues = (resourceData as Message)?.response;
    const messageDefaultValues = useMemo(() => {
        const messageDefaultValues = {};
        Object.keys(messageBody?.properties || {})?.map((field) => {
            const fieldProperties = messageBody?.properties[field];
            if (fieldProperties?.default) {
                messageDefaultValues[field] = fieldProperties?.default;
            } else if (fieldProperties?.element === "toggle") {
                messageDefaultValues[field] = false;
            }
            return true;
        });

        return messageDefaultValues;
    }, [messageBody?.properties]);
    const isMessage = resourceName === "message";
    const { defaultObjectTemplateSchema, isObjectTemplateSchemaLoading } =
        useDefaultObjectTemplate((resourceData as Custom_Object)?.object_template_id);

    const hasInputSchema =
        resourceConfig.resourceName === flowConfig.resourceName ||
        resourceConfig.resourceName === wrapperConfig.resourceName ||
        resourceConfig.resourceName === schedulerConfig.resourceName;
    const hasInputAndOutputSchema =
        resourceConfig.resourceName === flowConfig.resourceName ||
        resourceConfig.resourceName === schedulerConfig.resourceName;

    const getHelperValues = useCallback(
        (resourceData) => {
            return isCustomObject
                ? {
                    input_schema: defaultObjectTemplateSchema,
                    isSchemaLoading: isObjectTemplateSchemaLoading,
                }
                : hasInputAndOutputSchema
                    ? {
                        input_schema: schemaFormSchema,
                        input_value: isScheduler
                            ? (resourceData as Scheduler)?.configuration_schema
                            : (resourceData as any)?.input_schema,
                        output_schema: schemaFormSchema,
                        output_value: (resourceData as any)?.output_schema,
                    }
                    : hasInputSchema
                        ? {
                            input_schema: schemaFormSchema,
                            input_value: (resourceData as any)?.input_schema,
                        }
                        : {};
        },
        [
            defaultObjectTemplateSchema,
            isObjectTemplateSchemaLoading,
            hasInputAndOutputSchema,
            hasInputSchema,
            isCustomObject,
            isScheduler,
            schemaFormSchema,
        ]
    );
    const getFormValues = useCallback(
        (resourceData) => {
            const helperValues = getHelperValues(resourceData);

            return {
                initialValues: isMessage
                    ? { ...messageDefaultValues, ...messageResponseValues }
                    : getResourceDataInitialValues(
                        resourceConfig,
                        resourceData
                            ? {
                                ...resourceData,
                                ...helperValues,
                            }
                            : undefined
                    ),
                helperValues,
            };
        },
        [getHelperValues, isMessage, messageDefaultValues, messageResponseValues, resourceConfig]
    );

    const { initialValues } = useMemo(
        () => getFormValues(resourceData),
        [getFormValues, resourceData]
    );

    // Keep the live-conflict detector fresh: compare, per top-level field, whether the
    // user has locally modified it (current form value != old baseline) AND the incoming
    // push changes it (new server baseline != old baseline). Only a field that is BOTH is
    // a genuine conflict that should raise the warning; otherwise the push is silent.
    pushConflictRef.current = (incomingData) => {
        const state = formRef.current?.getState();
        if (!state) {
            return false;
        }
        const oldInitial = { ...(state.initialValues || {}) };
        const current = { ...(state.values || {}) };
        // canCommitOnSave is a UI-only helper field not present in the server transform.
        delete oldInitial.canCommitOnSave;
        delete current.canCommitOnSave;
        const newInitial = getFormValues(incomingData).initialValues || {};
        const keys = new Set([
            ...Object.keys(oldInitial),
            ...Object.keys(current),
            ...Object.keys(newInitial),
        ]);
        for (const key of keys) {
            const userModified = !isEqual(current[key], oldInitial[key]);
            const serverChanged = !isEqual(newInitial[key], oldInitial[key]);
            if (userModified && serverChanged) {
                return true;
            }
        }
        return false;
    };

    // when updating with ai "write_record" action
    const temporaryRecordData = useSelector(getTemporaryRecordData);
    const [temporaryValues, setTemporaryValues] = useState({});
    const [temporaryKey, setTemporaryKey] = useState("");

    const [hasFetchedInitialValues, setHasFetchedInitialValues] = useState(false);
    const [isMounted, setIsMounted] = useState(false);

    useEffect(() => {
        if (isMounted) {
            setTemporaryValues((Object.values(temporaryRecordData ?? {})[0] ?? {}) as any);
            setTemporaryKey(Object.keys(temporaryRecordData ?? {})[0]); // when creating with ai
        }
        setIsMounted(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMounted]);

    useEffect(() => {
        return () => {
            if (temporaryKey) {
                dispatch({
                    type: resourcesActions.CLEAR_TEMPORARY_RECORD_DATA,
                    temporaryKey,
                });
            }
        };
    }, [dispatch, temporaryKey]);

    const [hasTemporarySchemaFormValues, setHasTemporarySchemaFormValues] = useState(false);

    useEffect(() => {
        if (formRef.current && temporaryKey && hasFetchedInitialValues) {
            const values = {
                ...(resourceData ?? {}),
                ...temporaryValues,
            };
            const { initialValues: valuesWithTemporaryValues, helperValues } =
                getFormValues(values);

            if (formRef.current?.getState().pristine) {
                formRef.current.batch(() => {
                    for (const field in valuesWithTemporaryValues) {
                        if (
                            !(field in helperValues) &&
                            !isEqual(
                                valuesWithTemporaryValues[field],
                                formRef.current.getState().values[field]
                            )
                        ) {
                            formRef.current.change(field, valuesWithTemporaryValues[field]);

                            if (
                                field === valueAndSchemaInput ||
                                field === valueAndSchemaOutput ||
                                field === valueAndSchema
                            ) {
                                setHasTemporarySchemaFormValues(true);
                            }
                        }
                    }
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, temporaryKey, hasFetchedInitialValues]);

    const embeddedMessageForm = (
        <FinalForm
            onSubmit={(formData, form) => submitMessageResponse(formData, form)}
            initialValues={{ ...messageDefaultValues, ...messageResponseValues }}
            destroyOnUnregister
        >
            {({ handleSubmit, form, pristine, hasValidationErrors }) => (
                <>
                    <BlockingNavigationModal
                        navigate={history.push}
                        shouldBlockNavigation={!pristine}
                        titleText={strings.navigateAwayUnsavedPrompt}
                        submitForm={hasValidationErrors ? undefined : handleSubmit}
                    />
                    <MessageEditFields
                        formStructure={messageBody}
                        submitted={(resourceData as Message)?.is_submitted ?? false}
                        submitForm={handleSubmit}
                        form={form}
                        enableSave={!hasValidationErrors}
                    />
                </>
            )}
        </FinalForm>
    );

    const removeWriteOnlyFieldsRef = useRef<() => void>();

    // Saves the form on Ctrl + S
    useEffect(() => {
        const keydownHandler = (e) => onKeyDown(e, formId, removeWriteOnlyFieldsRef.current);
        document.addEventListener("keydown", keydownHandler);

        return () => {
            document.removeEventListener("keydown", keydownHandler);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (hasNewData) {
            /* to avoid dirtyFormWarning appear if hasNewData is true,
               but the values from the backend are equal to the form values*/
            const formValues = cloneDeep(formRef.current?.getState().values);
            delete formValues["canCommitOnSave"];
            const equalValues = isEqual(formValues, initialValues);
            if (equalValues) {
                resetDirty();
            }
        }
    }, [hasNewData, initialValues, resetDirty]);

    const onRefresh = () => {
        if (hasNewData) {
            formRef.current.setConfig("keepDirtyOnReinitialize", false);
            formRef.current.restart();
            formRef.current.setConfig("keepDirtyOnReinitialize", true);
            resetDirty();
            if (hasRecordSchemaForm) {
                // "Refresh" means (a) discard my edits and show server state
                // (operator-question-fe-record-modified-guard-fix-direction). form.restart()
                // resets react-final-form to server values, but the self-managed SchemaForm /
                // Monaco editors don't follow RFF's value stream, so they'd keep showing the
                // user's edits (edits preserved, form clean, Save disabled -- Stefan's bug #2).
                // Bumping refreshNonce is passed into the SchemaForm, which re-reads the server
                // values into its editors when the nonce changes.
                setRefreshNonce((n) => n + 1);
            }
        }
    };

    /**
     * Re-seed the open editors from the server after a "Migrate deprecations" apply.
     *
     * Migrate changes the record's script/value ON THE SERVER; the saga merges the
     * migrated record into the store, but the editors keep their pre-migration buffer
     * until a remount — the RFF-backed code editor keeps the stale field value
     * (keepDirtyOnReinitialize), and the self-managed SchemaForm/Monaco editors don't
     * follow RFF's value stream. So mirror the manual Refresh path: restart the form
     * to the now-stored server values (so the code editor adopts the migrated script)
     * and bump refreshNonce (so the SchemaForm editors re-read server state). Deferred
     * and keyed on storeRecordVersion so it runs only after the migrated record has
     * propagated into initialValues.
     * See operator-task-fe-migrate-deprecations-code-editor-no-refresh.
     */
    useEffect(() => {
        if (!pendingMigrateReseed) {
            return;
        }
        if (formRef.current) {
            formRef.current.setConfig("keepDirtyOnReinitialize", false);
            formRef.current.restart();
            formRef.current.setConfig("keepDirtyOnReinitialize", true);
        }
        resetDirty();
        if (hasRecordSchemaForm) {
            setRefreshNonce((n) => n + 1);
        }
        setPendingMigrateReseed(false);
    }, [pendingMigrateReseed, storeRecordVersion, hasRecordSchemaForm, resetDirty]);

    /**
     * Manual Refresh button (record page header) — re-fetch the record and show the
     * current server state. For static single-record screens (flow, setting, file,
     * connector, custom_object, ...) the detail view is fetch-once and goes stale;
     * this is the explicit "get me the latest" affordance Stefan asked for
     * (operator-task-fe-single-record-screen-refresh-button).
     *
     * Semantics:
     * - Pristine form (the common case — the user is just VIEWING, e.g. an
     *   operator-journal): adopt the freshly-fetched server values. Pristine
     *   top-level fields already re-initialise via react-final-form's
     *   keepDirtyOnReinitialize; we additionally clear any transient dirty-warning
     *   the coarse re-fetch comparison may raise (a manual refresh is not an edit
     *   conflict) and bump refreshNonce so the self-managed SchemaForm/Monaco
     *   editors re-read server state.
     * - Dirty form (the user has UNSAVED edits): we do NOT silently discard them.
     *   The re-fetch runs; if it changed a field the user edited, the existing
     *   dirty-form warning surfaces with its own discard-Refresh.
     */
    const onManualRefresh = useCallback(async () => {
        if (isManualRefreshing) return;
        // Read pristine BEFORE the re-fetch: it reflects whether the USER has edited,
        // independent of when react-final-form re-initialises after the fetch.
        const wasPristine = formRef.current?.getState()?.pristine ?? true;
        setIsManualRefreshing(true);
        try {
            await fetchResourceData();
        } finally {
            setIsManualRefreshing(false);
        }
        // Re-fetch the comment list too. It lives in a separate query that the
        // record re-fetch does not touch, so refresh it unconditionally (comments
        // are independent of the record form's dirty state).
        setCommentsRefreshNonce((n) => n + 1);
        if (wasPristine) {
            resetDirty();
            if (hasRecordSchemaForm) {
                setRefreshNonce((n) => n + 1);
            }
        }
    }, [isManualRefreshing, fetchResourceData, resetDirty, hasRecordSchemaForm]);

    // Show a warning if the form is dirty and new data has arrived from the backend
    const dirtyFormWarning = (
        <div className="dirtyFormWarning">
            <Callout
                text={
                    <div className="warningContent">
                        The record has been modified. Saving is not possible. Please take a note of
                        your changes and
                        <Button
                            onClick={() => onRefresh()}
                            label="Refresh"
                            variation="action"
                            icon={IconCollection.refresh}
                        />
                    </div>
                }
                type={"warning"}
                className="rootCallout"
            />
        </div>
    );

    /**
     * Controls whether the save button should be enabled manually.
     * Used when typing in "Code view" in schema form to enable save before blur event.
     *
     * @type {boolean}
     * @default false
     */
    const [isSaveEnabledManually, setSaveEnabledManually] = useState(false);

    const canCommitOnSaveInitialValue = useMemo(
        () =>
            canCommitOnSaveDefaultValue !== undefined
                ? { canCommitOnSave: canCommitOnSaveDefaultValue }
                : {},
        [canCommitOnSaveDefaultValue]
    );

    const fetchSearchTabsRef = useRef<() => Promise<any>>();

    const allUncommittedData = useContext(RouterContext).allUncommittedData;
    const uncommittedDataByPath = getUncommittedGitLogData({ allUncommittedData, resourceData });

    const gitLockData = useContext(RouterContext).gitLockData;

    const isGitLocked = useMemo(() => {
        const gitLockedId = isContainerType(resourceName)
            ? recordId
            : resourceData?.project_id ?? resourceData?.bundle_id ?? resourceData?.workspace_id;
        if (!gitLockedId) return false;
        const recordGitLockData = gitLockData[gitLockedId];

        return recordGitLockData?.state === "WRITE_START";
    }, [
        gitLockData,
        recordId,
        resourceData?.bundle_id,
        resourceData?.project_id,
        resourceData?.workspace_id,
        resourceName,
    ]);

    const memoizedParams = useMemo(() => ({ id: recordId }), [recordId]);
    /**
     * Memoized props for the Schedules tab in the Flow resource.
     * Used to configure the "Used By" tab for schedules.
     */
    const scheduleTabProps = useMemo(
        () => ({
            referencedRecord: scheduleConfig.resourceName,
            queryParams: { query: scheduleQuery },
            buttonProps: {
                ButtonComponent: () => (
                    <CreateScheduleButton
                        recordId={recordId}
                        isFieldDisabled={isCreateScheduleDisabled(resourceData)}
                    />
                ),
                docs: {
                    title: "Schedules",
                    url: "/docs/scheduling#schedule",
                },
            },
        }),
        [recordId, resourceData]
    );

    const inputSchemaFieldName = useMemo(
        () => (isScheduler ? "configuration_schema" : "input_schema"),
        [isScheduler]
    );

    // Memoize the initial form values to avoid unnecessary re-renders
    const memoizedInitialValues = useMemo(
        () => ({
            ...initialValues,
            ...canCommitOnSaveInitialValue,
        }),
        [canCommitOnSaveInitialValue, initialValues]
    );

    const additionalFieldProps = useMemo(
        () =>
            isFileType
                ? {
                    setContentShown: setShowFileContent,
                    isContentShown: canLoadFileContent,
                    isContentAlwaysHidden: isFileContentAlwaysHidden,
                    // Adaptive content display resolved from the server-detected MIME
                    // type (operator-task-fe-file-mimetype-adaptive-display).
                    lang: fileDisplay.lang,
                    fileDisplayCategory: fileDisplay.category,
                    fileContentType,
                }
                : hasRecordSchemaForm
                    ? {
                        isDirty: hasNewData && formRef.current?.getState().pristine,
                        resetDirty,
                        canUpdateFieldOnSchemaChanges: isSchedule,
                        setHasTemporarySchemaFormValues,
                        hasTemporarySchemaFormValues,
                        // Incremented by onRefresh; drives the SchemaForm to re-read server
                        // values into its editors (Refresh = discard my edits, show server state).
                        refreshNonce,
                    }
                    : isUserType
                        ? { isSSPHidden }
                        : {},
        [
            canLoadFileContent,
            fileContentType,
            fileDisplay,
            hasNewData,
            hasRecordSchemaForm,
            hasTemporarySchemaFormValues,
            isFileContentAlwaysHidden,
            isFileType,
            isSSPHidden,
            isSchedule,
            isUserType,
            resetDirty,
            // Forces recompute of isDirty after onRefresh's form.restart() so the SchemaForm
            // re-syncs its self-managed editors to server state (Refresh = discard my edits).
            refreshNonce,
        ]
    );

    const additionalSearchQueryParams = useMemo(() => {
        const isProjectDeleted = !!(
            (resourceConfig.resourceName === projectConfig.resourceName ||
                resourceConfig.resourceName === bundleConfig.resourceName) &&
            resourceData?.deleted_at
        );
        return isProjectDeleted ? { allow_deleted: true } : {};
    }, [resourceConfig.resourceName, resourceData?.deleted_at]);

    const searchTabsFilter = useMemo(() => {
        return resourceConfig?.layout
            ?.find((layout) => layout?.partial === "SearchTabs")
            ?.filter?.(recordId);
    }, [recordId, resourceConfig?.layout]);

    /**
     * Tracks whether the form has unsaved changes.
     * Used to disable tab switching when there are unsaved changes.
     *
     * @type {boolean}
     * @default false
     */
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [hasChangesInSchemaTab, setHasChangesInSchemaTab] = useState(false);

    const { defaultSchemaValues } = useMatchingFields({ activeTab: resourceName });

    const isTabDisabled = useMemo(
        () =>
            hasUnsavedChanges ||
            Object.values(nestedFormSaveData).length > 0 ||
            hasChangesInSchemaTab,
        [hasChangesInSchemaTab, hasUnsavedChanges, nestedFormSaveData]
    );

    const iconSelector = useMemo(() => {
        if (customObjectConfig.resourceName === resourceName && recordId) {
            return getIconSelector(resourceName, recordId);
        }
        return () => null;
    }, [resourceName, recordId]);

    const getStateTabsCountHint = useCallback(
        (activeTab) => {
            switch (activeTab) {
                case usedBySchedulesList:
                    return (resourceData as Flow)?.schedule_list_flow_id?.schedule_count;
                case usedByWebhooksList:
                    return (resourceData as Flow)?.webhook_list_flow_id?.webhook_count;
                case metadataTabKey:
                    return resourceData?.record_metadata_list_record_id?.record_metadata_count;
                case commentsTabKey:
                    return (resourceData as any)?.record_comment_list_record_id
                        ?.record_comment_count;
                case wrappersTabKey:
                    return (resourceData as any)?.resource_wrapper_list_resource_id
                        ?.resource_wrapper_count;
                default:
                    return 0;
            }
        },
        [resourceData]
    );

    const tabsWithCountHint = useMemo(() => {
        if (resourceConfig?.layout?.find((layout) => layout?.partial === "tabs")) {
            const configTabs =
                resourceConfig?.layout?.find((layout) => layout?.partial === "tabs")?.config
                    ?.tabs || [];
            return configTabs.map((tab) => {
                return {
                    ...tab,
                    countHint: getStateTabsCountHint(tab.uid),
                };
            });
        }
        return [];
    }, [getStateTabsCountHint, resourceConfig?.layout]);

    const customObjectIcon = useSelector(iconSelector, shallowEqual);

    const replaceSearchParams = useCallback(() => {
        const by = prefersIdInUrl(resourceName) ? "id" : "name";
        const ref = by === "name" ? resourceData?.name : recordId;
        if (!ref) {
            return;
        }
        history.replace({ search: formatSearchParams({ [by]: ref }) });
    }, [history, recordId, resourceData?.name, resourceName]);

    // Switching between a record's own in-page tabs is not "navigating away" —
    // the record instance and its dirty form state are preserved. But leaving
    // the logs tab runs `replaceSearchParams` (a `history.replace` that strips
    // the ?logs flag, leaving just ?id=X), which the unsaved-changes guard would
    // otherwise treat as a route change and prompt on (false positive Stefan hit
    // switching to the Comments tab). Allow navigations that stay on the SAME
    // record's plain single-record view (same pathname, same id/name, no
    // sub-page flag or canvas) through without prompting; genuine navigation away
    // — a different record, a sub-page/canvas view, or another screen — still
    // prompts.
    const isSameRecordViewNavigation = useCallback(
        (nextLocation: { pathname: string; search: string }): boolean => {
            if (nextLocation.pathname !== history.location.pathname) {
                return false;
            }
            const by = prefersIdInUrl(resourceName) ? "id" : "name";
            const ref = by === "name" ? resourceData?.name : recordId;
            if (!ref) {
                return false;
            }
            const nextParams = parseSearchParams(nextLocation.search || "");
            if (nextParams[by] !== String(ref)) {
                return false;
            }
            // A pure in-page tab switch stays on the SAME record view: it adds
            // no view-determining flag and at most drops ?logs (leaving the logs
            // tab runs replaceSearchParams). The plain single-record view lands
            // on ?id=X (no flags); the dashboard config editor keeps its ?edit
            // flag, so a logs→metadata/comments switch there lands on the same
            // ?edit view and must NOT be treated as a view navigation
            // (operator-task-fe-dashboard-config-tab-switch-no-navigation).
            // Dropping a non-logs view flag (leaving ?edit/?canvas) or adding a
            // flag is genuine navigation and still prompts when the form is dirty.
            return isInPageTabSwitch(
                history.location.search,
                nextLocation.search
            );
        },
        [history, recordId, resourceData?.name, resourceName]
    );

    // On the workspace route the record id is not yet known while auth/check
    // and the subsequent user-data websocket message are still in flight. Show
    // a loader instead of momentarily rendering the not-found Error screen for
    // the (still undefined) workspace id.
    if (isWorkspace && !uid) {
        return <PageLoader />;
    }

    if ((!resourceData && !isFetching && !isLoading) || isNotFound) {
        return <Error />;
    }

    // Render the main form and dynamic layouts based on the resource configuration
    return (
        <>
            {/* Helmet sets the body class for styling and loading state */}
            <Helmet
                bodyAttributes={{
                    class: `generic-record ${isFetching ? ` generic-record--fetching` : ""}`,
                }}
            />
            {/* Main form for editing the resource */}
            <FinalForm
                onSubmit={async (formData, form) => {
                    let changedValues = updatedDiff(form.getState().initialValues, formData);

                    // find formData keys that are not present in initialValues and add them to changedValues
                    Object.keys(formData).forEach((key) => {
                        if (initialValues[key] === undefined) {
                            changedValues[key] = formData[key];
                        }
                    });

                    // do not allow setting branch_name to an empty string for git-enabled records (see https://app.clickup.com/t/861mzk85y)
                    if (changedValues["branch_name"] === "" && formData.git_url) {
                        form.change("branch_name", null);
                        delete changedValues["branch_name"];
                    }
                    if (formData[valueAndSchema]) {
                        changedValues[valueAndSchema] = formData[valueAndSchema];
                    }
                    if (formData[valueAndSchemaInput]) {
                        if (
                            !isEqual(
                                formData[valueAndSchemaInput],
                                form.getState().initialValues[valueAndSchemaInput]
                            )
                        ) {
                            changedValues[valueAndSchemaInput] = formData[valueAndSchemaInput];
                        }
                    }
                    if (formData[valueAndSchemaOutput]) {
                        if (
                            !isEqual(
                                formData[valueAndSchemaOutput],
                                form.getState().initialValues[valueAndSchemaOutput]
                            )
                        ) {
                            changedValues[valueAndSchemaOutput] = formData[valueAndSchemaOutput];
                        }
                    }
                    // Drop untouched write-only fields (empty secret/password
                    // inputs) so an unrelated edit cannot silently overwrite a
                    // stored secret with "". See stripUntouchedWriteOnlyFields.
                    changedValues = stripUntouchedWriteOnlyFields(
                        changedValues,
                        resourceConfig.form,
                        writeOnlyFieldsWithData
                    );
                    // A stored credential is bound to its destination: changing
                    // the URL / server CA / TLS checks clears it (the backend
                    // rejects the save otherwise). Ask before clearing it, so a
                    // secret is never cleared by accident. See credentialBinding.
                    const credentialClearing = getCredentialsClearedByDestinationChange(
                        resourceName,
                        changedValues,
                        form.getState().initialValues,
                        writeOnlyFieldsWithData
                    );
                    if (credentialClearing) {
                        const fieldLabel = (name: string) =>
                            String(
                                resourceConfig.form?.find((field: any) => field?.name === name)
                                    ?.fieldProps?.label ?? name
                            );
                        const changed = credentialClearing.changedFields.map(fieldLabel);
                        const cleared = credentialClearing.credentialsToClear.map(fieldLabel);
                        const confirmed = await new Promise<boolean>((resolve) => {
                            showConfirmModal({
                                title: `Clear the stored ${cleared.join(" and ")}?`,
                                content: `Changing ${changed.join(
                                    " and "
                                )} clears the stored ${cleared.join(
                                    " and "
                                )}, so it is not sent to the new destination. To keep using it, cancel and enter it again.`,
                                confirmText: "Save and clear",
                                cancelButtonProps,
                                className: "confirmationModal",
                                centered: true,
                                onConfirm: () => resolve(true),
                                onCancel: () => resolve(false),
                            });
                        });
                        if (!confirmed) {
                            return;
                        }
                        for (const name of credentialClearing.credentialsToClear) {
                            changedValues[name] = "";
                        }
                    }
                    const values = { ...changedValues, id: formData.id };

                    // For a message answer we must submit the full form values, not the
                    // updatedDiff()-filtered `changedValues`: an unanswered message has no
                    // prior value for the answer fields or `is_submitted` in initialValues,
                    // so updatedDiff() silently strips exactly the values the user just
                    // entered (hasOwnProperty(initialValues, key) guard). Pass the raw
                    // formData through, matching the embedded execution-screen message form
                    // (embeddedMessageForm), which submits unfiltered formData and works.
                    // submitMessageResponse targets the correct record id via recordId and
                    // strips is_submitted before building the response payload.
                    return isMessage
                        ? submitMessageResponse(formData, form)
                        : submitRecordForm(values, form);
                }}
                initialValues={memoizedInitialValues}
                keepDirtyOnReinitialize
                // Only re-run this (large) render prop for form-level flags that the
                // shell actually depends on. `values` changes on every keystroke and
                // is consumed only by ResourceEditFields, which is fed via a scoped
                // FormSpy below; subscribing to it here would re-render the whole tree.
                subscription={{
                    pristine: true,
                    submitting: true,
                    hasValidationErrors: true,
                }}
                validate={(values) => {
                    const errors: { [key: string]: any } = {};
                    // setting branch_name to an empty string should show an error (see https://app.clickup.com/t/861mzk85y)
                    if (
                        values.branch_name === "" &&
                        initialValues.branch_name?.length &&
                        values.git_url
                    ) {
                        errors.branch_name = "Required field.";
                    }
                    return errors;
                }}
                mutators={{
                    changeCanCommitOnSave: changeCanCommitOnSaveMutator,
                    changeSchemaFormValues,
                    changeScheduler,
                }}
            >
                {({ handleSubmit, form, pristine, submitting, hasValidationErrors }) => {
                    formRef.current = form;

                    // Remove write-only fields from the form before submitting.
                    // Reads live form state rather than a subscribed `values` prop so
                    // this render prop need not re-run on every keystroke.
                    removeWriteOnlyFieldsRef.current = () => {
                        const currentValues = form.getState().values;
                        for (const key in writeOnlyFieldsWithData) {
                            if (currentValues[key] !== undefined) {
                                form.change(key, undefined);
                            }
                        }
                    };

                    return (
                        <form
                            id={formId}
                            name={formId}
                            onSubmit={(e) => {
                                e.preventDefault();
                                if (!pristine || isSaveEnabledManually) handleSubmit();
                            }}
                            autoComplete="off"
                        >
                            <BlockingNavigationModal
                                navigate={history.push}
                                shouldBlockNavigation={
                                    !(
                                        isCustomObject &&
                                        pristine === false &&
                                        hasNewData === true
                                    ) && !pristine
                                }
                                titleText={strings.navigateAwayUnsavedPrompt}
                                submitForm={hasValidationErrors ? undefined : handleSubmit}
                                priority={NAVIGATION_PRIORITIES.PARENT_FORM}
                                allowNavigation={isSameRecordViewNavigation}
                            />
                            {/* hidden value to add { commit: true } to the graphql query */}
                            {isGitEnabled && <Field name="canCommitOnSave">{() => <></>}</Field>}

                            {pristine === false && hasNewData === true ? dirtyFormWarning : null}
                            {isRecordPopoverOpen && popoverRecord && (
                                <RecordPrompt
                                    data={popoverRecord}
                                    isOpen={isRecordPopoverOpen}
                                    onConfirm={() => {
                                        setRecordPopoverOpen(false);
                                        fetchResourceData();
                                    }}
                                    onCancel={() => {
                                        setRecordPopoverOpen(false);
                                    }}
                                    isScrollable={popoverRecord.resourceName === "plugin_action"}
                                />
                            )}
                            {resourceConfig?.layout?.map((layout) => {
                                if (layout?.partial === "ExecutionSingleStatus") {
                                    return (
                                        <div key="executionStatus">
                                            <ExecutionSingleStatus
                                                key="ExecutionSingleStatus"
                                                data={resourceData as Execution}
                                                showErrorScreen={showErrorScreen}
                                            />
                                            <div style={{ paddingLeft: "3em" }}>
                                                <h3>
                                                    {
                                                        (resourceData as Execution)
                                                            ?.message_id_message?.subject
                                                    }
                                                </h3>
                                                {embeddedMessageForm}
                                            </div>
                                        </div>
                                    );
                                }
                                if (layout?.partial === "SearchTabs") {
                                    if (!resourceData) return null;
                                    return (
                                        <SearchTabs
                                            key="SearchTabs"
                                            filter={searchTabsFilter}
                                            name={layout?.name ?? ""}
                                            showLocationColumn={layout?.showLocationColumn ?? false}
                                            additionalQueryParameters={additionalSearchQueryParams}
                                            isProjectReadOnly={isReadOnly}
                                            fetchSearchTabsRef={fetchSearchTabsRef}
                                            id={recordId}
                                            isRecordDisabled={isFieldDisabled}
                                            isGitEnabled={isGitEnabled}
                                            isBundle={isBundle}
                                            resourceKeyName={resourceName}
                                            hasUnsavedChanges={hasUnsavedChanges}
                                            setHasUnsavedChanges={setHasUnsavedChanges}
                                            bootstrapUserName={bootstrapUserName}
                                        />
                                    );
                                }
                                if (layout?.partial === "pageHeaderForm") {
                                    return (
                                        <ResourceSinglePageHeader
                                            key="pageHeaderForm"
                                            handleSubmit={handleSubmit}
                                            resetForm={() => {
                                                resetFinalForm(form);
                                            }}
                                            pristine={pristine}
                                            resourceData={resourceData}
                                            resourceName={resourceName}
                                            fetchResourceData={fetchResourceData}
                                            onRefresh={isExecution ? undefined : onManualRefresh}
                                            isRefreshing={isManualRefreshing}
                                            iri={iri}
                                            resourceConfig={resourceConfig}
                                            titleIcon={customObjectIcon}
                                            isDisabled={isDisabled}
                                            fetchSearchTabs={fetchSearchTabsRef.current}
                                            uncommittedDataByPath={uncommittedDataByPath}
                                            isGitLocked={isGitLocked}
                                            {...layout?.config}
                                            hasValidationErrors={hasValidationErrors}
                                            isGitEnabled={isGitEnabled}
                                            isFetching={isFetching || isLoading}
                                            bootstrapUserName={bootstrapUserName}
                                        >
                                            <RecordActionButtons
                                                key="topActions"
                                                submitForm={async () =>
                                                    (await handleSubmit()?.then((data) => {
                                                        removeWriteOnlyFieldsRef.current?.();
                                                        return data;
                                                    })) ?? {}
                                                }
                                                resourceData={resourceData}
                                                resourceTypeName={resourceName}
                                                enableSave={
                                                    !(pristine === false && hasNewData === true) &&
                                                    (!pristine || isSaveEnabledManually) &&
                                                    !hasValidationErrors
                                                }
                                                isLoading={submitting || isFetching}
                                                formPristine={pristine}
                                                fetchResourceData={fetchResourceData}
                                                hasValidationErrors={hasValidationErrors}
                                                nestedFormSaveData={nestedFormSaveData}
                                                form={form}
                                                canCommitOnSaveDefaultValue={
                                                    canCommitOnSaveDefaultValue
                                                }
                                                setDirtyForm={setDirtyForm}
                                                onMigrateDeprecationsApplied={
                                                    onMigrateDeprecationsApplied
                                                }
                                                uncommittedDataByPath={uncommittedDataByPath}
                                                isGitLocked={isGitLocked}
                                                bootstrapUserName={bootstrapUserName}
                                                iri={iri}
                                            />
                                        </ResourceSinglePageHeader>
                                    );
                                }

                                if (layout?.partial === "dashboard") {
                                    return (
                                        <PageContent key="dashboard">
                                            <div className="flex flex-wrap">
                                                {resourceConfig?.dashboard?.map(
                                                    (dashboardItem: DashboardItemConfig) => {
                                                        if (isDashboardSection(dashboardItem)) {
                                                            return (
                                                                <DashboardCollapsibleSection
                                                                    key={dashboardItem.uid}
                                                                    section={dashboardItem}
                                                                    resourceName={
                                                                        resourceConfig.resourceName
                                                                    }
                                                                    recordId={recordId}
                                                                    resourceData={resourceData ?? null}
                                                                    onExpandedChange={
                                                                        setIsDashboardPanelExpanded
                                                                    }
                                                                />
                                                            );
                                                        }
                                                        const widget =
                                                            dashboardItem as DashboardWidgetConfig;
                                                        return (
                                                            <WidgetWrapper
                                                                dashboardItem={widget}
                                                                recordId={recordId ?? "global"}
                                                                resourceData={resourceData}
                                                                key={widget.uid}
                                                            />
                                                        );
                                                    }
                                                )}
                                            </div>
                                        </PageContent>
                                    );
                                }
                                if (layout?.partial === "form") {
                                    if (!resourceData) {
                                        return <Placeholder key="form" />;
                                    }
                                    return (
                                        <PageContent key="form">
                                            {/* Scope the per-keystroke `values` subscription to
                                                just this subtree; the outer shell stays subscribed
                                                to form-level flags only. */}
                                            <FormSpy
                                                subscription={{
                                                    values: true,
                                                    initialValues: true,
                                                }}
                                            >
                                                {({ values, initialValues }) => (
                                                    <ResourceEditFields
                                                        formStructure={filteredFormStructure}
                                                        isDisabled={isDisabled}
                                                        isReadOnly={isReadOnly}
                                                        writeOnlyFieldsWithData={
                                                            writeOnlyFieldsWithData
                                                        }
                                                        fetchResourceData={fetchResourceData}
                                                        resourceTypeName={resourceName}
                                                        recordId={recordId}
                                                        setNestedFormSaveData={
                                                            setNestedFormSaveData
                                                        }
                                                        nestedFormSaveData={nestedFormSaveData}
                                                        additionalFieldProps={additionalFieldProps}
                                                        enableSaveManually={setSaveEnabledManually}
                                                        currentValues={values}
                                                        // Persisted (backend-returned) values, so a
                                                        // field can gate its read-only state on the
                                                        // saved value rather than the unsaved form
                                                        // value (e.g. schedule config lock).
                                                        persistedValues={initialValues}
                                                        isLoaded={!isFetching && !isLoading}
                                                        defaultValues={defaultSchemaValues}
                                                    />
                                                )}
                                            </FormSpy>
                                        </PageContent>
                                    );
                                }
                                if (layout?.partial === "generateForm") {
                                    return (
                                        <PageContent key="generateForm">
                                            <MessageEditFields
                                                formStructure={messageBody}
                                                submitted={
                                                    (resourceData as Message)?.is_submitted ?? false
                                                }
                                                submitForm={handleSubmit}
                                                form={form}
                                                enableSave={!hasValidationErrors}
                                            />
                                        </PageContent>
                                    );
                                }
                                if (layout?.partial === "tabs") {
                                    if (!resourceData) return null;

                                    const [activeTabContent] = layout?.config?.content.filter(
                                        (item) => item.uid === activeTab
                                    );
                                    const isInputSchemaTabActive =
                                        activeTabContent?.type === inputSchemaContentType;
                                    const isOutputSchemaTabActive =
                                        activeTabContent?.type === outputSchemaContentType;
                                    const isConnectionType =
                                        (resourceData as Execution).type === "CONNECTION";
                                    const usedByProps =
                                        resourceName === customObjectConfig.resourceName
                                            ? {
                                                queryParams: {
                                                    query: customObjectUsedByListQuery,
                                                    params: memoizedParams,
                                                },
                                                useFilter: false,
                                                referencedRecord: customObjectConfig.resourceName,
                                            }
                                            : resourceName === settingConfig.resourceName &&
                                                activeTab === holdingTheLockTabKey
                                                ? {
                                                    usedByRecordType:
                                                        "setting.execution_id_execution",
                                                    queryParams: {
                                                        query: settingHoldingLockListQuery,
                                                        params: memoizedParams,
                                                    },
                                                    useFilter: false,
                                                    showSingleValueAsList: true,
                                                }
                                                : resourceName === flowConfig.resourceName &&
                                                    activeTab === usedBySchedulesList
                                                    ? scheduleTabProps
                                                    : resourceName === flowConfig.resourceName &&
                                                        activeTab === usedByWebhooksList
                                                        ? {
                                                            referencedRecord: webhookConfig.resourceName,
                                                            buttonProps: {
                                                                label: createWebhook.label,
                                                                icon: createWebhook.icon,
                                                                isDisabled:
                                                                    createWebhook.isDisabled(resourceData),
                                                                onClick: () =>
                                                                    createWebhook.action({
                                                                        flowId: recordId,
                                                                        history,
                                                                    }),
                                                                docs: {
                                                                    title: "Webhooks",
                                                                    url: "/docs/webhooks",
                                                                },
                                                            },
                                                        }
                                                        : {};

                                    return (
                                        <PageContent
                                            key="tabs"
                                            className="page-content--min-height"
                                        >
                                            <StateTabs
                                                activeTab={activeTab}
                                                setActiveTab={setActiveTab}
                                                tabs={tabsWithCountHint}
                                                hiddenTabs={layout?.config?.hiddenTabs || []}
                                                title={layout?.config?.tabsTitle}
                                                isConnectionType={isConnectionType}
                                                isDisabled={isTabDisabled}
                                                replaceSearchParams={replaceSearchParams}
                                            />
                                            {isConnector ? (
                                                <Field
                                                    name={valueAndSchema}
                                                    id={valueAndSchema}
                                                    isEqual={(valueA, valueB) => {
                                                        return isEqual(valueA, valueB);
                                                    }}
                                                    validate={(value) => {
                                                        try {
                                                            parseYaml(value.values);
                                                            return undefined;
                                                        } catch (error: any) {
                                                            const typedError: YAMLException = error;
                                                            const errorMessage = `${typedError?.name}: ${typedError?.reason}`;
                                                            return errorMessage;
                                                        }
                                                    }}
                                                    remappingSubmitDto={(
                                                        value: SchematizedValues
                                                    ) => {
                                                        return [
                                                            {
                                                                key: "value",
                                                                value: value.values,
                                                            },
                                                            {
                                                                key: "input_schema",
                                                                value: value.schema,
                                                            },
                                                        ];
                                                    }}
                                                >
                                                    {({ input, fieldProps, fieldAttr, meta }) =>
                                                        activeTabContent?.type ===
                                                            valueAndSchemaContentType ? (
                                                            <Form.Row>
                                                                <LazySchemaFormFormComponent
                                                                    input={input}
                                                                    fieldProps={{
                                                                        ...fieldProps,
                                                                        lang: "yaml",
                                                                        canEnableSaveManually: true,
                                                                        // has_deprecation is declared only on the
                                                                        // script-bearing child types (Flow/Wrapper/
                                                                        // Scheduler/Connector), not on the polymorphic
                                                                        // Record base. This branch renders those
                                                                        // script-bearing types, so cast to read the
                                                                        // optional field.
                                                                        hasDeprecation: (
                                                                            resourceData as {
                                                                                has_deprecation?: boolean;
                                                                            } | null
                                                                        )?.has_deprecation,
                                                                        omitRequired: true,
                                                                        ...additionalFieldProps,
                                                                    }}
                                                                    fieldAttr={{
                                                                        ...fieldAttr,
                                                                        disabled: isFieldDisabled,
                                                                    }}
                                                                    meta={meta}
                                                                />
                                                            </Form.Row>
                                                        ) : null
                                                    }
                                                </Field>
                                            ) : null}
                                            {hasInputSchema ? (
                                                <Field
                                                    name={valueAndSchemaInput}
                                                    id={valueAndSchemaInput}
                                                    isEqual={(valueA, valueB) => {
                                                        return isEqual(valueA, valueB);
                                                    }}
                                                    validate={(value) => {
                                                        try {
                                                            parseYaml(value.values);
                                                            return undefined;
                                                        } catch (error: any) {
                                                            const typedError: YAMLException = error;
                                                            const errorMessage = `${typedError?.name}: ${typedError?.reason}`;
                                                            const validationError = {
                                                                path: "",
                                                                message: errorMessage,
                                                                messageWithPath: errorMessage,
                                                            };
                                                            return validationError;
                                                        }
                                                    }}
                                                    remappingSubmitDto={(
                                                        value: SchematizedValues
                                                    ) => {
                                                        if (value) {
                                                            return [
                                                                {
                                                                    key: inputSchemaFieldName,
                                                                    value: value.values,
                                                                },
                                                            ];
                                                        } else return [];
                                                    }}
                                                >
                                                    {({ input, fieldProps, fieldAttr, meta }) =>
                                                        isInputSchemaTabActive ? (
                                                            <Form.Row>
                                                                <LazySchemaFormFormComponent
                                                                    input={input}
                                                                    fieldProps={{
                                                                        ...fieldProps,
                                                                        lang: "yaml" as FormComponentPropLanguages,
                                                                        columnDirection: true,
                                                                        testFormButton: true,
                                                                        enrichSchemaButton: true,
                                                                        canEnableSaveManually: true,
                                                                        ...additionalFieldProps,
                                                                    }}
                                                                    fieldAttr={{
                                                                        ...fieldAttr,
                                                                        disabled: isFieldDisabled,
                                                                    }}
                                                                    meta={meta}
                                                                />{" "}
                                                            </Form.Row>
                                                        ) : null
                                                    }
                                                </Field>
                                            ) : null}
                                            {hasInputAndOutputSchema ? (
                                                <Field
                                                    name={valueAndSchemaOutput}
                                                    id={valueAndSchemaOutput}
                                                    isEqual={(valueA, valueB) => {
                                                        return isEqual(valueA, valueB);
                                                    }}
                                                    validate={(value) => {
                                                        try {
                                                            parseYaml(value.values);
                                                            return undefined;
                                                        } catch (error: any) {
                                                            const typedError: YAMLException = error;
                                                            const errorMessage = `${typedError?.name}: ${typedError?.reason}`;
                                                            const validationError = {
                                                                path: "",
                                                                message: errorMessage,
                                                                messageWithPath: errorMessage,
                                                            };
                                                            return validationError;
                                                        }
                                                    }}
                                                    remappingSubmitDto={(
                                                        value: SchematizedValues
                                                    ) => {
                                                        if (value) {
                                                            return [
                                                                {
                                                                    key: "output_schema",
                                                                    value: value.values,
                                                                },
                                                            ];
                                                        } else return [];
                                                    }}
                                                >
                                                    {({ input, fieldProps, fieldAttr, meta }) =>
                                                        isOutputSchemaTabActive ? (
                                                            <Form.Row>
                                                                <LazySchemaFormFormComponent
                                                                    input={input}
                                                                    fieldProps={{
                                                                        ...fieldProps,
                                                                        lang: "yaml" as FormComponentPropLanguages,
                                                                        columnDirection: true,
                                                                        testFormButton: true,
                                                                        enrichSchemaButton: true,
                                                                        canEnableSaveManually: true,
                                                                        ...additionalFieldProps,
                                                                    }}
                                                                    fieldAttr={{
                                                                        ...fieldAttr,
                                                                        disabled: isFieldDisabled,
                                                                    }}
                                                                    meta={meta}
                                                                />
                                                            </Form.Row>
                                                        ) : null
                                                    }
                                                </Field>
                                            ) : null}
                                            {activeTabContent?.type === wrappersContentType && (
                                                <div
                                                    className="resourceWrapperList"
                                                    key="resourceWrapperList"
                                                >
                                                    <ResourceWrapperContainer
                                                        setNestedFormSaveData={
                                                            setNestedFormSaveData
                                                        }
                                                        nestedFormSaveData={nestedFormSaveData}
                                                    />
                                                </div>
                                            )}
                                            {activeTabContent?.type === "codeEditor" && (
                                                <>
                                                    <LazyResizeableCodeEditor
                                                        key={activeTab}
                                                        input={
                                                            {
                                                                value: get(
                                                                    resourceData,
                                                                    activeTabContent?.value
                                                                ),
                                                            } as any
                                                        }
                                                        meta={{}}
                                                        fieldProps={{
                                                            resourceTypeName: resourceName,
                                                            recordId: recordId,
                                                            ...activeTabContent?.fieldProps,
                                                        }}
                                                        fieldAttr={{
                                                            disabled:
                                                                isFieldDisabled ||
                                                                activeTabContent?.fieldProps
                                                                    ?.isReadOnly,
                                                        }}
                                                    />
                                                </>
                                            )}
                                            {activeTabContent?.type === "schemaForm" && (
                                                <ExecutionInputOutputValueTab
                                                    activeTab={activeTab as "input" | "output"}
                                                    activeTabContent={activeTabContent}
                                                    resourceData={resourceData as Execution}
                                                    recordId={recordId}
                                                    isExecution={isExecution}
                                                    iri={iri}
                                                    key={`${activeTab}-schemaForm`}
                                                    isExecutionRunning={isExecutionRunning}
                                                />
                                            )}
                                            {activeTabContent?.type === "readonlySchemaForm" && (
                                                <ExecutionSchemaTab
                                                    key={`${activeTab}-readonlySchemaForm`}
                                                    activeTab={activeTab}
                                                    schemaField={
                                                        activeTabContent?.value as
                                                            | "input_schema"
                                                            | "output_schema"
                                                    }
                                                    resourceData={resourceData as Execution}
                                                />
                                            )}
                                            {activeTabContent?.type === "executionsListing" && (
                                                <ExecutionsListing
                                                    key={activeTab}
                                                    parentExecutionId={recordId}
                                                    setFilterOnMount={true}
                                                />
                                            )}
                                            {activeTabContent?.type === "savepointsListing" && (
                                                <SavepointsListing
                                                    key={activeTab}
                                                    execution={resourceData}
                                                />
                                            )}
                                            {activeTabContent?.type ===
                                                executionRelatedRecordsContentType && (
                                                    <ExecutionRelatedRecordListing key={activeTab} />
                                                )}
                                            {activeTabContent?.type === "logsListing" &&
                                                resourceName &&
                                                recordId && (
                                                    <RecordLogList
                                                        resourceName={resourceName}
                                                        id={recordId}
                                                        setFilterOnMount={true}
                                                    />
                                                )}
                                            {activeTabContent?.type === usedByContentKey &&
                                                resourceName &&
                                                recordId && (
                                                    <UsedBySingleRecordList
                                                        resourceName={resourceName}
                                                        id={recordId}
                                                        {...usedByProps}
                                                    />
                                                )}
                                            {activeTabContent?.type === metadataContentType &&
                                                resourceName &&
                                                recordId && (
                                                    <MetadataList
                                                        id={recordId}
                                                        isDisabled={isFieldDisabled}
                                                        setHasUnsavedChanges={setHasUnsavedChanges}
                                                        resourceName={resourceName}
                                                    />
                                                )}
                                            {activeTabContent?.type === commentsContentType &&
                                                resourceName &&
                                                recordId && (
                                                    <CommentsPanel
                                                        id={recordId}
                                                        isDisabled={isFieldDisabled}
                                                        setHasUnsavedChanges={setHasUnsavedChanges}
                                                        resourceName={resourceName}
                                                        refreshNonce={commentsRefreshNonce}
                                                        allowNavigation={
                                                            isSameRecordViewNavigation
                                                        }
                                                    />
                                                )}
                                        </PageContent>
                                    );
                                }
                                if (layout?.partial === "pluginActionList") {
                                    return (
                                        <PluginActionList
                                            resourceData={resourceData as Plugin}
                                            onResourceClick={onResourceClick}
                                            createSubrecord={createPluginAction}
                                            isFieldDisabled={isFieldDisabled}
                                            fetchResourceData={fetchResourceData}
                                            key="pluginActionList"
                                        />
                                    );
                                }
                                if (layout?.partial === "userGitPublicKeyList") {
                                    return (
                                        <UserGitPublicKeyList
                                            resourceData={resourceData as User}
                                            onResourceClick={onResourceClick}
                                            createSubrecord={createUserGitPublicKey}
                                            isOwnUser={isOwnUser}
                                            fetchResourceData={fetchResourceData}
                                            key="userGitPublicKeyList"
                                        />
                                    );
                                }
                                if ((layout as any)?.partial === "userGitPat") {
                                    const userId = (resourceData as User)?.id;
                                    const disabled = !isOwnUser || resourceData?.deleted_at != null;
                                    return (
                                        <UserGitPatPanel
                                            userId={userId}
                                            userData={resourceData}
                                            disabled={disabled}
                                            refreshData={fetchResourceData}
                                        />
                                    );
                                }
                                if ((layout as any)?.partial === "userApiKey") {
                                    const userId = (resourceData as User)?.id;
                                    const disabled =
                                        isFieldDisabled || resourceData?.deleted_at != null;
                                    return (
                                        <UserApiKeyPanel
                                            userId={userId}
                                            disabled={disabled}
                                            key="userApiKey"
                                        />
                                    );
                                }
                                if (layout?.partial === "rolePermissionList") {
                                    return (
                                        <RolePermissionList
                                            resourceData={resourceData as Role}
                                            onResourceClick={onResourceClick}
                                            createSubrecord={createRolePermission}
                                            isFieldDisabled={isFieldDisabled}
                                            fetchResourceData={fetchResourceData}
                                            updateRolePermissionQuery={updateRolePermissionQuery}
                                            rolePermissionInitialQuery={rolePermissionInitialQuery}
                                            key="rolePermissionList"
                                        />
                                    );
                                }
                                if (layout?.partial === "objectTemplateAttributeList") {
                                    return (
                                        <ObjectTemplateAttributeList
                                            resourceData={resourceData as Object_Template}
                                            onResourceClick={onResourceClick}
                                            objectTemplateList={objectTemplateList}
                                            createSubrecord={createObjectTemplateAttribute}
                                            isFieldDisabled={isFieldDisabled}
                                            fetchResourceData={fetchResourceData}
                                            updateAttributeListQuery={updateAttributeListQuery}
                                            initialQuery={attributeInitialQuery}
                                            key="objectTemplateAttributeList"
                                        />
                                    );
                                }
                                if (layout?.partial === "customObjectList") {
                                    return (
                                        <CustomObjectList
                                            resourceData={resourceData as Object_Template}
                                            recordId={recordId}
                                            objectTemplateList={objectTemplateList}
                                            fetchResourceData={fetchResourceData}
                                            updateCustomObjectListQuery={
                                                updateCustomObjectListQuery
                                            }
                                            history={history}
                                            key="customObjectList"
                                        />
                                    );
                                }
                                if (layout?.partial === "roleIdentityList" && resourceData?.id) {
                                    return (
                                        <RoleIdentityList
                                            key="roleIdentityList"
                                            updateIdentityQuery={updateIdentityQuery}
                                            identityQuery={identityQuery}
                                            isActionButtonDisabled={!!resourceData?.deleted_at}
                                            roleId={resourceData.id}
                                        />
                                    );
                                }
                                return null;
                            })}
                            {isSchedule ? <OnChangeSchedulerId form={form} /> : null}
                            <SchemaTabDirtyTracker onChange={setHasChangesInSchemaTab} />
                        </form>
                    );
                }}
            </FinalForm>
        </>
    );
};

/**
 * Wrap the ResourceSingle component with an error boundary for robust error handling.
 */
const WrappedResourceSingle = withErrorBoundary(ResourceSingle, {
    FallbackComponent: ErrorBoundary,
    onError(error) {
        console.error(error);
    },
});

export default WrappedResourceSingle;
